import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { OrbitControls as ThreeOrbitControls } from "three/examples/jsm/controls/OrbitControls";
// Postprocessing imports removed to avoid alpha issues with transparency

const TAU = Math.PI * 2;

// Interactive POINTS Logo Component
function PointsLogo() {
	const canvasRef = useRef(null);
	const mouse = useRef({ x: -9999, y: -9999, down: false, hover: false, hoverSub: false });
	const state = useRef({ title: [], titleBounds: { x: 0, y: 0, w: 0, h: 0 }, subBounds: { x: 0, y: 0, w: 0, h: 0 } });
	const subLabelRef = useRef({ red: null, green: null, blue: null, white: null, w: 0, h: 0, font: '', text: '.ENGINEERING' });

	// Target intense brand blue
	const BRAND_BLUE = { r: 0, g: 81, b: 255 }; // #0051FF

	useEffect(() => {
		const canvas = canvasRef.current;
		const ctx = canvas.getContext("2d");
		let animationId;

		const resize = () => {
			const rect = canvas.parentElement.getBoundingClientRect();
			const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
			canvas.width = rect.width * dpr;
			canvas.height = rect.height * dpr;
			canvas.style.width = rect.width + "px";
			canvas.style.height = rect.height + "px";
			initTitle();
		};

		const initTitle = async () => {
			const { width: W, height: H } = canvas;
			const off = document.createElement("canvas");
			off.width = W;
			off.height = H;
			const octx = off.getContext("2d");
			octx.clearRect(0, 0, W, H);

			const title = "POINTS";
			const fontSize = Math.min(W * 0.15, H * 0.6);
			octx.fillStyle = "#fff";
			octx.font = `900 ${fontSize}px 'Inter', 'Helvetica Neue', system-ui, sans-serif`;
			octx.textAlign = "center";
			octx.textBaseline = "middle";
			
				// Draw main title (POINTS) and sample with normal dot size
				const scaleX = 1.4;
				octx.save();
				octx.translate(W / 2, H / 2);
				octx.scale(scaleX, 1);
				octx.fillText(title, 0, 0);
				octx.restore();

				const dataMain = octx.getImageData(0, 0, W, H);
				const step = Math.max(2, Math.floor(Math.sqrt((W * H) / 12000)));
				const mainDotSize = Math.max(1.5, step * 0.40);
				const pts = [];
				let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

				for (let y = 0; y < dataMain.height; y += step) {
					for (let x = 0; x < dataMain.width; x += step) {
						if (dataMain.data[(y * dataMain.width + x) * 4 + 3] > 128) {
							minX = Math.min(minX, x);
							minY = Math.min(minY, y);
							maxX = Math.max(maxX, x);
							maxY = Math.max(maxY, y);
							pts.push({ x, y, bx: x, by: y, vx: 0, vy: 0, s: mainDotSize, heat: 0 });
						}
					}
				}

				// (ENGINEERING) sublabel will be rendered as regular overlay text, not particles

				state.current.title = pts;
				state.current.titleBounds = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };

				// Prepare sublabel offscreen canvases and bounds
				const bounds = state.current.titleBounds;
				if (bounds && bounds.w > 0 && bounds.h > 0) {
					const dprLocal = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
					const pad = bounds.h * 0.06;
					const subFontSize = Math.max(12 * dprLocal, bounds.h * 0.36);
					const fontSpec = `800 ${subFontSize}px 'Inter', 'Helvetica Neue', system-ui, sans-serif`;
					const text = subLabelRef.current.text;
					octx.save();
					octx.font = fontSpec;
					octx.textAlign = 'left';
					octx.textBaseline = 'top';
					const textWidth = octx.measureText(text).width;
					octx.restore();
					const subX = bounds.x + bounds.w - pad - textWidth;
					const subY = bounds.y + bounds.h + pad;
					state.current.subBounds = { x: subX, y: subY, w: textWidth, h: subFontSize * 1.1 };

					const buildCanvas = (color) => {
						const c = document.createElement('canvas');
						c.width = Math.ceil(textWidth);
						c.height = Math.ceil(subFontSize * 1.2);
						const cx = c.getContext('2d');
						cx.fillStyle = color;
						cx.font = fontSpec;
						cx.textAlign = 'left';
						cx.textBaseline = 'top';
						cx.fillText(text, 0, 0);
						return c;
					};
					subLabelRef.current = {
						red: buildCanvas('rgba(255,0,0,0.8)'),
						green: buildCanvas('rgba(0,255,0,0.8)'),
						blue: buildCanvas('rgba(0,0,255,0.8)'),
						white: buildCanvas('rgba(255,255,255,0.96)'),
						w: Math.ceil(textWidth),
						h: Math.ceil(subFontSize * 1.2),
						font: fontSpec,
						text
					};
				}
		};

		const onMove = (e) => {
			const rect = canvas.getBoundingClientRect();
			const dpr = window.devicePixelRatio || 1;
			const clientX = e.touches ? e.touches[0].clientX : e.clientX;
			const clientY = e.touches ? e.touches[0].clientY : e.clientY;
			mouse.current.x = (clientX - rect.left) * dpr;
			mouse.current.y = (clientY - rect.top) * dpr;
			const { x, y, w, h } = state.current.titleBounds;
			mouse.current.hover =
				mouse.current.x >= x &&
				mouse.current.x <= x + w &&
				mouse.current.y >= y &&
				mouse.current.y <= y + h;
			const sb = state.current.subBounds || { x: 0, y: 0, w: 0, h: 0 };
			mouse.current.hoverSub =
				mouse.current.x >= sb.x &&
				mouse.current.x <= sb.x + sb.w &&
				mouse.current.y >= sb.y &&
				mouse.current.y <= sb.y + sb.h;
		};

		const animate = () => {
			ctx.clearRect(0, 0, canvas.width, canvas.height);
			const dpr = window.devicePixelRatio || 1;

			const influence = mouse.current.hover ? (mouse.current.down ? 480 : 110) * dpr : 0;
			const strength = mouse.current.hover ? (mouse.current.down ? 36.8 : 4.6) : 0;
			const mx = mouse.current.x, my = mouse.current.y;

			for (const p of state.current.title) {
				if (influence > 0) {
					const dx = mx - p.x;
					const dy = my - p.y;
					const d = Math.hypot(dx, dy) + 0.0001;
					if (d < influence) {
						const f = (strength / d) * 2.0;
						p.vx -= (dx / d) * f;
						p.vy -= (dy / d) * f;
						const proximity = Math.max(0, 1 - d / influence);
						// Very short falloff for punchy interaction
						const targetHeat = Math.pow(proximity, 3.6);
						p.heat += (targetHeat - p.heat) * 0.22; // quick charge-up
						p.heat = Math.max(0, Math.min(1, p.heat));
					}
				}
				p.heat *= 0.94; // slightly slower decay for smoother fade-out
				p.vx += (p.bx - p.x) * 0.012;
				p.vy += (p.by - p.y) * 0.012;
				p.vx *= 0.92;
				p.vy *= 0.92;
				p.x += p.vx;
				p.y += p.vy;
			}

			ctx.globalCompositeOperation = "source-over";
			for (const p of state.current.title) {
				const r = (p.s + 0.8) * dpr;
				const t = Math.max(0, Math.min(1, p.heat));
				const rr = Math.round(255 + (BRAND_BLUE.r - 255) * t);
				const gg = Math.round(255 + (BRAND_BLUE.g - 255) * t);
				const bb = Math.round(255 + (BRAND_BLUE.b - 255) * t);
				ctx.fillStyle = `rgba(${rr},${gg},${bb},0.98)`;
				ctx.beginPath();
				ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
				ctx.fill();
			}

			// Draw animated sublabel text with heat shimmer and RGB trail
			const bounds = state.current.titleBounds;
			if (bounds && bounds.w > 0 && bounds.h > 0 && subLabelRef.current.white) {
				const pad = bounds.h * 0.06;
				const x = state.current.subBounds.x || (bounds.x + bounds.w - pad - subLabelRef.current.w);
				const y = state.current.subBounds.y || (bounds.y + bounds.h + pad);
				const srcs = [subLabelRef.current.red, subLabelRef.current.green, subLabelRef.current.blue];
				const whiteSrc = subLabelRef.current.white;
				const sep = 1.5 * dpr;
				const slice = Math.max(2, Math.floor(2 * dpr));
				const amp = 2.2 * dpr;
				const t = (performance.now() || 0) * 0.001;
				const width = subLabelRef.current.w;
				const height = subLabelRef.current.h;

				const drawSliced = (image, offsetX) => {
					for (let sy = 0; sy < height; sy += slice) {
						const ny = sy / height;
						const wobble = Math.sin(ny * 10.0 + t * 3.0) * amp;
						const dx = wobble + offsetX;
						ctx.drawImage(image, 0, sy, width, Math.min(slice, height - sy), x + dx, y + sy, width, Math.min(slice, height - sy));
					}
				};

				ctx.save();
				ctx.globalCompositeOperation = 'screen';
				drawSliced(srcs[0], -sep);
				drawSliced(srcs[1], 0);
				drawSliced(srcs[2], sep);
				ctx.restore();

				ctx.save();
				ctx.globalCompositeOperation = 'source-over';
				drawSliced(whiteSrc, 0);
				ctx.restore();
			}

			animationId = requestAnimationFrame(animate);
		};

		// Improve touch interactions on mobile without affecting desktop
		canvas.style.touchAction = 'none';
		canvas.style.webkitTapHighlightColor = 'transparent';

		resize();
		window.addEventListener("resize", resize);
		canvas.addEventListener("pointermove", onMove, { passive: true });
		canvas.addEventListener("pointerdown", () => mouse.current.down = true);
		canvas.addEventListener("pointerup", () => mouse.current.down = false);
		canvas.addEventListener("pointerleave", () => {
			mouse.current.hover = false;
			mouse.current.down = false;
		});
		canvas.addEventListener("pointercancel", () => {
			mouse.current.down = false;
		});

		// Touch handlers to prevent page scroll while interacting on mobile
		const onTouchMove = (e) => {
			if (e.cancelable) e.preventDefault();
			const rect = canvas.getBoundingClientRect();
			const dpr = window.devicePixelRatio || 1;
			const t = e.touches && e.touches[0];
			if (!t) return;
			mouse.current.x = (t.clientX - rect.left) * dpr;
			mouse.current.y = (t.clientY - rect.top) * dpr;
			mouse.current.hover = true;
		};
		const onTouchStart = (e) => {
			if (e.cancelable) e.preventDefault();
			mouse.current.down = true;
			onTouchMove(e);
		};
		const onTouchEnd = (e) => {
			if (e.cancelable) e.preventDefault();
			mouse.current.down = false;
			mouse.current.hover = false;
		};
		canvas.addEventListener('touchstart', onTouchStart, { passive: false });
		canvas.addEventListener('touchmove', onTouchMove, { passive: false });
		canvas.addEventListener('touchend', onTouchEnd, { passive: false });
		canvas.addEventListener('touchcancel', onTouchEnd, { passive: false });

		animate();

		return () => {
			cancelAnimationFrame(animationId);
			window.removeEventListener("resize", resize);
			canvas.removeEventListener('touchstart', onTouchStart);
			canvas.removeEventListener('touchmove', onTouchMove);
			canvas.removeEventListener('touchend', onTouchEnd);
			canvas.removeEventListener('touchcancel', onTouchEnd);
			canvas.removeEventListener("pointermove", onMove);
			canvas.removeEventListener("pointerdown", () => {});
			canvas.removeEventListener("pointerup", () => {});
		};
	}, []);

	return <canvas ref={canvasRef} className="w-full h-full touch-none select-none" />;
}

// Lightweight GLTF viewer for Services tile
function FloatingModelViewer({ modelUrl = '/fob.glb', className, fitMultiplier = 0.53, tilt = { x: 0.08, y: -3.14, z: -0.34 }, rotateSpeed = 0.26, positionOffset = { x: -0.065, y: 0, z: 0 }, initialAngle = -0.7 }) {
	const containerRef = useRef(null);
	const frameRef = useRef(0);
	const pivotRef = useRef(null);
	const rotateSpeedRef = useRef(rotateSpeed);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const scene = new THREE.Scene();
		scene.background = null;
		// Ensure canvas element itself is transparent in DOM
		container.style.background = 'transparent';
		const pivot = new THREE.Group();
		scene.add(pivot);
		pivotRef.current = pivot;

		const width = container.clientWidth;
		const height = container.clientHeight;
		const camera = new THREE.PerspectiveCamera(45, width / height, 0.01, 10000);
		camera.position.set(0.8, 0.7, 1.8);

		const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, premultipliedAlpha: true, logarithmicDepthBuffer: true });
		renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
		renderer.setSize(width, height);
		renderer.setClearColor(0x000000, 0);
		if (renderer.setClearAlpha) renderer.setClearAlpha(0);
		renderer.domElement.style.background = 'transparent';
		// Color-accurate rendering with mild lift (avoid washout)
		try { renderer.outputEncoding = THREE.sRGBEncoding; } catch (_) { try { renderer.outputColorSpace = THREE.SRGBColorSpace; } catch (_) {} }
		try { renderer.toneMapping = THREE.ACESFilmicToneMapping; } catch (_) {}
		renderer.toneMappingExposure = 1.02;
		container.appendChild(renderer.domElement);

		// Transparent-safe bloom pipeline
		// Bloom removed to ensure transparency; we'll use a safe CSS blur instead

		// Lights tuned to keep saturation (less ambient, stronger key + subtle rim)
		scene.add(new THREE.AmbientLight(0xffffff, 0.5));
		const hemi = new THREE.HemisphereLight(0xbfdfff, 0x225566, 0.25);
		scene.add(hemi);
		const dir = new THREE.DirectionalLight(0xffffff, 1.35);
		dir.position.set(2, 3, 2);
		scene.add(dir);
		const rim = new THREE.DirectionalLight(0xffffff, 0.55);
		rim.position.set(-2, -1, -2);
		scene.add(rim);

		const loader = new GLTFLoader();
		let root = null;
		loader.load(
			modelUrl,
			(gltf) => {
				root = gltf.scene || gltf.scenes[0];
				if (!root) return;

				// Center model at origin first
				const initialBox = new THREE.Box3().setFromObject(root);
				const initialCenter = initialBox.getCenter(new THREE.Vector3());
				root.position.sub(initialCenter);

				// Heuristic: orient the longest axis to vertical (Y)
				const size = new THREE.Vector3();
				initialBox.getSize(size);
				const maxAxis = size.x > size.y ? (size.x > size.z ? 'x' : 'z') : (size.y > size.z ? 'y' : 'z');
				if (maxAxis === 'z') {
					root.rotation.x += -Math.PI / 2; // map Z -> Y
				} else if (maxAxis === 'x') {
					root.rotation.z += Math.PI / 2; // map X -> Y
				}

				pivot.add(root);

				// Preserve original material colors; no emissive lift

				// Initial tilt/position
				pivot.rotation.x = tilt.x;
				pivot.rotation.y = tilt.y + initialAngle;
				pivot.rotation.z = tilt.z;
				pivot.position.set(positionOffset.x || 0, positionOffset.y || 0, positionOffset.z || 0);

				// Frame based on oriented model
				const box = new THREE.Box3().setFromObject(pivot);
				const sphere = box.getBoundingSphere(new THREE.Sphere());
				const radius = Math.max(0.0001, sphere.radius);
				const fov = (camera.fov * Math.PI) / 180;
				const distance = (radius / Math.sin(fov / 2)) * fitMultiplier;
				camera.near = Math.max(0.001, distance / 1000);
				camera.far = Math.max(100, distance * 100);
				camera.updateProjectionMatrix();
				camera.position.set(distance, distance * 0.4, distance * 1.2);
				camera.lookAt(0, 0, 0);
			},
			undefined,
			() => {}
		);

		const onResize = () => {
			const w = container.clientWidth;
			const h = container.clientHeight;
			renderer.setSize(w, h, false);
			camera.aspect = w / h;
			camera.updateProjectionMatrix();
			renderer.setClearColor(0x000000, 0);
		};
		window.addEventListener('resize', onResize);

		const animate = () => {
			frameRef.current = requestAnimationFrame(animate);
			const p = pivotRef.current;
			if (p) {
				p.rotation.y += rotateSpeedRef.current * 0.01;
			}
			renderer.render(scene, camera);
		};
		animate();

		return () => {
			cancelAnimationFrame(frameRef.current);
			window.removeEventListener('resize', onResize);
			// no composer to dispose
			renderer.dispose();
			if (renderer.domElement && renderer.domElement.parentNode) {
				renderer.domElement.parentNode.removeChild(renderer.domElement);
			}
		};
	}, [modelUrl, fitMultiplier]);

	// Update pivot transforms and speed on prop changes without re-initializing scene
	useEffect(() => {
		rotateSpeedRef.current = rotateSpeed;
		const p = pivotRef.current;
		if (!p) return;
		p.rotation.x = tilt.x;
		p.rotation.y = tilt.y + initialAngle;
		p.rotation.z = tilt.z;
		p.position.set(positionOffset.x || 0, positionOffset.y || 0, positionOffset.z || 0);
	}, [tilt.x, tilt.y, tilt.z, positionOffset.x, positionOffset.y, positionOffset.z, rotateSpeed, initialAngle]);

	return <div ref={containerRef} className={className || "w-full h-full"} />;
}

// Image component that tries multiple extensions (jpg, png, jpeg, webp) in /public/MG
function SmartImage({ dir = 'MG', name, alt, className }) {
	const publicUrl = process.env.PUBLIC_URL || '';
	const dirs = Array.from(new Set([dir, dir.toLowerCase(), dir.toUpperCase()]));
	const baseCandidates = dirs.map((d) => `${publicUrl}/${d}`);
	const extCandidates = ['jpg', 'png', 'jpeg', 'webp', 'JPG', 'PNG', 'JPEG', 'WEBP'];
	const [baseIndex, setBaseIndex] = useState(0);
	const [extIndex, setExtIndex] = useState(0);

	const src = `${baseCandidates[baseIndex]}/${name}.${extCandidates[extIndex]}`;

	const handleError = () => {
		setExtIndex((prev) => {
			if (prev < extCandidates.length - 1) return prev + 1;
			if (baseIndex < baseCandidates.length - 1) {
				setBaseIndex(baseIndex + 1);
				return 0;
			}
			return prev;
		});
	};

	return (
		<img
			src={src}
			onError={handleError}
			alt={alt}
			loading="lazy"
			className={(className ? className + ' ' : '') + 'w-full h-auto rounded-xl border border-white/15 shadow-sm'}
		/>
	);
}

// Detailed content for the Merigold project
function MerigoldContent() {
	return (
		<div className="text-white/90">
			<h2 className="text-3xl md:text-4xl font-black mb-6 uppercase tracking-wider">Marigold Church Molding Restoration Project</h2>

			<p className="mb-4">The project began with a unique challenge: a 100+ year-old church in Merigold, Mississippi was missing a six-foot section of historic molding. The profile was no longer manufactured, and sourcing such a small section was nearly impossible. To preserve the building's architectural integrity, we were asked to recreate the molding from scratch.</p>
			<p className="mb-6">We received measurements of the missing section along with dimensions from existing trim.</p>
			<div className="mb-8">
				<SmartImage name="0" alt="Measurement reference of missing section" />
				<p className="mt-2 text-sm text-white/70">Reference measurements of the missing molding profile.</p>
			</div>

			<p className="mb-6">Additionally, the client shipped us several original pieces that had fallen off the church for detailed 3D scanning.</p>
			<div className="mb-10">
				<SmartImage name="1" alt="Original molding pieces for scanning" />
				<p className="mt-2 text-sm text-white/70">Original fragments provided for capture.</p>
			</div>

			<h3 className="text-xl font-bold uppercase tracking-wide mb-3">3D Scanning & Digital Reconstruction</h3>
			<p className="mb-4">The pieces were scanned in their existing condition, and we digitally rebuilt the molding patterns one fragment at a time. Once compiled, the scans provided an approximate recreation of the section in true 3D space.</p>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
				<SmartImage name="2" alt="Scan of molding fragment" className="max-h-72 object-contain" />
				<SmartImage name="3" alt="Reconstruction of molding pattern" className="max-h-72 object-contain" />
			</div>
			<div className="mb-10">
				<SmartImage name="4" alt="Compiled 3D recreation of molding section" />
			</div>

			<p className="mb-6">To prepare the files for production, we performed heavy remeshing and retopology, cleaning up the scan data into precise, manufacturable 3D models. Each unique pattern was carefully reconstructed and placed onto a recreated base section to ensure accuracy and consistency.</p>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
				<SmartImage name="5" alt="Remeshing and cleanup process" className="max-h-72 object-contain" />
				<SmartImage name="6" alt="Retopology and reconstructed base section" className="max-h-72 object-contain" />
			</div>

			<h3 className="text-xl font-bold uppercase tracking-wide mb-3">Prototyping & Alignment</h3>
			<p className="mb-4">We then 3D printed test prototypes to verify how the new sections aligned with the original trim in the church. After confirming proper fit, we moved on to aesthetic testing.</p>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
				<SmartImage name="7" alt="3D printed prototype for alignment" className="max-h-72 object-contain" />
				<SmartImage name="8" alt="Alignment check against original trim" className="max-h-72 object-contain" />
			</div>

			<h3 className="text-xl font-bold uppercase tracking-wide mb-3">Paint & Finish Matching</h3>
			<p className="mb-4">Since the existing trim had aged over many decades, the client requested we match the finish as closely as possible. We created paint prototypes on the printed molding to blend seamlessly with the historic trim.</p>
			<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
				<SmartImage name="9" alt="Paint prototype 1" className="max-h-72 object-contain" />
				<SmartImage name="10" alt="Paint prototype 2" className="max-h-72 object-contain" />
				<SmartImage name="11" alt="Paint prototype 3" className="max-h-72 object-contain" />
				<SmartImage name="12" alt="Paint prototype 4" className="max-h-72 object-contain" />
			</div>

			<p className="mb-2 font-medium">Result</p>
			<p className="mb-2">The Marigold Church project demonstrates how digital technologies like 3D scanning, remeshing, and additive manufacturing can preserve historic craftsmanship.</p>
			<p className="">By combining modern tools with traditional design sensibilities, we were able to restore a vital architectural detail and help maintain the church's original character for future generations.</p>
		</div>
	);
}

function UMMCContent() {
	return (
		<div className="text-white/90">
			<h2 className="text-3xl md:text-4xl font-black mb-6 uppercase tracking-wider">UMMC Custom Camera Mount Project</h2>

			<p className="mb-4">We were approached by a resident surgeon at the University of Mississippi Medical Center (UMMC) with a request to design and build a custom camera mount for her Surgitel headband. The goal was to integrate a secondary camera—an Insta360 Go3s—without interfering with the primary head-mounted camera already in use. This additional camera provides a wider angle of view to support education and training in the medical field.</p>

			<p className="mb-4">The project presented unique design considerations due to the camera's modular battery pack. Midway through development, the camera's pack was swapped for a different version, but thanks to the built-in tripod mount, we were able to adapt quickly. This change actually streamlined the design, allowing us to finalize a solution with minimal schedule impact.</p>

			<p className="mb-3">The final mount secures along the square tube of the headband. It locks its vertical (z-axis) position via a set screw and threaded insert embedded directly into the printed component.</p>
			<div className="mb-8">
				<SmartImage dir="UMMC" name="5" alt="Set screw and threaded insert on printed component" />
				<p className="mt-2 text-sm text-white/70">Set screw and embedded threaded insert for z-axis locking.</p>
			</div>

			<p className="mb-4">The camera itself pivots smoothly up and down using its tripod mount, which is adjusted with a large blue knob for quick and reliable positioning.</p>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
				<SmartImage dir="UMMC" name="3" alt="Pivoting action using tripod mount - view 1" />
				<SmartImage dir="UMMC" name="4" alt="Pivoting action using tripod mount - view 2" />
			</div>

			<p className="">Trials are now underway in the operating room to validate performance and usability.</p>
		</div>
	);
}

function HallContent() {
	return (
		<div className="text-white/90">
			<h2 className="text-3xl md:text-4xl font-black mb-6 uppercase tracking-wider">Rifle Stock Scanning & CAD Recreation</h2>

			<p className="mb-4">A client approached us with a two-part request: 3D scan two separate rifle stocks, and fully recreate one of them as a clean CAD model. The stock selected for CAD reconstruction is a bench-rest competition rifle stock, distinguished by its wide, flat foregrip designed for stable, precision shooting from a rest.</p>
			<div className="mb-8">
				<img src="/gun0.jpeg" alt="Completed bench-rest competition rifle" loading="lazy" className="w-full h-auto rounded-xl border border-white/15 shadow-sm" />
				<p className="mt-2 text-sm text-white/70">A typical bench-rest competition rifle, showing the wide flat foregrip characteristic of the style.</p>
			</div>

			<h3 className="text-xl font-bold uppercase tracking-wide mb-3">3D Scanning</h3>
			<p className="mb-4">We began by scanning both stocks to capture their full geometry. The second stock was scanned for archival and reference purposes only.</p>
			<div className="mb-8">
				<img src="/gun5.png" alt="Rifle stock scanned for reference" loading="lazy" className="w-full h-auto rounded-xl border border-white/15 shadow-sm" />
				<p className="mt-2 text-sm text-white/70">The second rifle stock, scanned for reference.</p>
			</div>

			<p className="mb-4">The primary stock — the bench-rest competition model — was scanned in detail to serve as the foundation for CAD reconstruction.</p>
			<div className="mb-10">
				<img src="/gun1.jpeg" alt="Bench-rest stock to be recreated in CAD" loading="lazy" className="w-full h-auto rounded-xl border border-white/15 shadow-sm" />
				<p className="mt-2 text-sm text-white/70">The competition stock selected for full CAD recreation.</p>
			</div>

			<h3 className="text-xl font-bold uppercase tracking-wide mb-3">Scan vs. CAD Comparison</h3>
			<p className="mb-4">The raw scan captures every surface imperfection and artifact, while the CAD model distills the geometry into clean, manufacturable surfaces. The comparison below highlights the difference between organic scan data and engineered topology.</p>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
				<div>
					<img src="/gun2.png" alt="Raw 3D scan of rifle stock" loading="lazy" className="w-full h-auto rounded-xl border border-white/15 shadow-sm" />
					<p className="mt-2 text-sm text-white/70">Raw 3D scan data.</p>
				</div>
				<div>
					<img src="/gun3.png" alt="Clean CAD recreation of rifle stock" loading="lazy" className="w-full h-auto rounded-xl border border-white/15 shadow-sm" />
					<p className="mt-2 text-sm text-white/70">Clean CAD recreation.</p>
				</div>
			</div>

			<h3 className="text-xl font-bold uppercase tracking-wide mb-3">Surfacing & Reconstruction</h3>
			<p className="mb-4">The scan data was brought into surfacing software where each contour was traced and rebuilt as precise, parametric geometry ready for manufacturing or further modification.</p>
			<div className="mb-10">
				<img src="/gun4.jpeg" alt="Surfacing the scan data in CAD software" loading="lazy" className="w-full h-auto rounded-xl border border-white/15 shadow-sm" />
				<p className="mt-2 text-sm text-white/70">Surfacing the scan data in software.</p>
			</div>

			<h3 className="text-xl font-bold uppercase tracking-wide mb-3">Final Model</h3>
			<p className="mb-4">The completed CAD model can be explored below. Rotate, zoom, and inspect the finished geometry.</p>
			<div className="mb-4">
				<WireframeModelViewer className="w-full h-[280px] md:h-[420px] rounded-2xl border border-white/20 bg-white/5 overflow-hidden" fitMultiplier={0.8} modelUrl="/gun.glb" />
			</div>
		</div>
	);
}

function TurcotteContent() {
	return (
		<div className="text-white/90">
			<h2 className="text-3xl md:text-4xl font-black mb-6 uppercase tracking-wider">Prosthetic Cover Recreation Project</h2>

			<p className="mb-4">A client approached us with a unique challenge: to recreate a prosthetic cover through 3D modeling.</p>

			<p className="mb-4">We began by scanning the exterior of the original piece to capture its shape and surface detail.</p>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
				<SmartImage dir="TURCOTTE" name="1" alt="Exterior of original prosthetic cover" className="max-h-72 object-contain" />
				<SmartImage dir="TURCOTTE" name="2" alt="Surface detail of the exterior scan" className="max-h-72 object-contain" />
			</div>

			<p className="mb-4">However, the interior geometry proved to be more complex. To accurately replicate it, we created a silicone cast of the inside. This cast was then scanned separately, giving us precise data of the interior structure.</p>
			<div className="mb-8">
				<SmartImage dir="TURCOTTE" name="3" alt="Silicone cast of the interior for scanning" />
			</div>

			<p className="mb-4">With both the interior and exterior scans in hand, we combined the datasets into a complete digital mesh. From there, we carried out surfacing and CAD reconstruction, resulting in a highly accurate 3D model of the prosthetic cover.</p>
			<div className="mb-10">
				<SmartImage dir="TURCOTTE" name="4" alt="Combined interior and exterior reconstruction" className="max-h-80 object-contain" />
			</div>

			<p className="">This project presented significant challenges and required us to think outside the box, blending scanning, casting, and advanced digital modeling techniques to deliver a precise and functional recreation.</p>
		</div>
	);
}

function QuickSpoolContent() {
	return (
		<div className="text-white/90">
			<h2 className="text-3xl md:text-4xl font-black mb-6 uppercase tracking-wider">QuickSpool — Heavy-Duty String Spool</h2>

			<p className="mb-4">A client in the concrete business approached us with a pain point he runs into on every job: every string spool on the market is a nightmare to deal with. The line falls off the side, the rotation is sloppy, and there is no fast way to wind everything back up at the end of the day. He came to us with a set of rough dimensions and a clear list of must-haves, and asked us to design something better.</p>

			<p className="mb-4">Working from his critical parameters, we designed a spool noticeably larger than anything currently on the market, with a profile shaped to hold string cleanly without losing tension or letting line walk off the flange.</p>

			<p className="mb-6">The handle is oversized and ergonomic for hand winding, but the real time-saver is the integrated 1/4" hex bit on the opposite end — every concrete crew already has an impact wrench on site, so they can chuck QuickSpool straight into the driver and wind the entire line back up in seconds. Lips and grooves at the spool ends lock the loose string in place so it stays tidy between jobs.</p>

			<div className="mb-8">
				<img src="/quickspool.JPEG" alt="QuickSpool prototype in high-visibility green" loading="lazy" className="w-full h-auto rounded-xl border border-white/15 shadow-sm" />
				<p className="mt-2 text-sm text-white/70">The prototype, printed in high-visibility green so it does not get lost when crews pack up at the end of a job.</p>
			</div>

			<p className="mb-2 font-medium">Result</p>
			<p className="">The client — a long-time owner-operator in the concrete industry — has been running the prototype on real jobs and is pleased with the final product. QuickSpool turns a daily annoyance into a few seconds of impact-driver work.</p>
		</div>
	);
}

// Detailed content for the PS-250 project (BEYOND section)
function PS250Content() {
	return (
		<div className="text-zinc-800">
			<h2 className="text-3xl md:text-4xl font-black mb-6 uppercase tracking-wider">PS-250 — A Console Reborn</h2>

			<p className="mb-4">The PS-250 began with an unusual piece of silicon. To meet the demand of the cryptocurrency boom, Sony adapted the semi-custom APU at the heart of the PlayStation 5 into a dedicated mining accelerator — the BC-250. Sold in volume to ASRock and others, these boards are, in essence, de-tuned PlayStation 5s: the same Zen 2 / RDNA 2 lineage, repackaged as blade-style GPU miners rather than game consoles.</p>

			<p className="mb-4">When the mining market cooled, those boards flooded the surplus channel at a fraction of the cost of comparable hardware. A dedicated enthusiast community formed around them, steadily reverse-engineering the platform and reclaiming its capabilities. The most significant milestone came recently, when all 40 compute units were unlocked — restoring the graphics horsepower the board had been derated from.</p>

			<p className="mb-6">The PS-250 is our effort to turn that bare, inexpensive board into something genuinely livable: a couch-ready machine with the spirit of a Steam Machine, built for a fraction of the price. The design was driven by four goals:</p>

			<ol className="list-decimal list-inside space-y-2 mb-6">
				<li><span className="font-semibold">Structure.</span> A purpose-built enclosure that holds the board, power, and peripherals together as one coherent unit rather than a loose collection of parts.</li>
				<li><span className="font-semibold">Thermals.</span> A deliberate airflow path that keeps the APU comfortable under sustained load.</li>
				<li><span className="font-semibold">Durability.</span> A rugged, transportable chassis that can be moved and handled without fragility.</li>
				<li><span className="font-semibold">Quality of life.</span> A true living-room experience — power on from a controller, clean shutdown, and everything else managed from the couch.</li>
			</ol>

			<p className="mb-6">The result is a low-cost, console-like system that takes a board originally destined for obsolescence and gives it a second life as an approachable, everyday machine.</p>

			<div className="mb-8">
				<PlainModelViewer className="w-full h-[280px] md:h-[420px] rounded-2xl border border-black/10 bg-black/5 overflow-hidden" fitMultiplier={0.8} modelUrl="/ps250.glb" />
				<p className="mt-2 text-sm text-zinc-500">The PS-250 enclosure. Drag to rotate, scroll to zoom.</p>
			</div>

			<h3 className="text-xl font-bold uppercase tracking-wide mb-3">Build It Yourself</h3>
			<p className="mb-4">The full build guide, printable models, and the remote power-controller firmware are open and available below.</p>
			<div className="flex flex-col sm:flex-row gap-3">
				<a
					href="https://www.printables.com/model/1767985-ps-250-bc-250-case"
					target="_blank"
					rel="noopener noreferrer"
					className="inline-flex items-center justify-center gap-2 rounded-xl border border-black/15 bg-black/5 hover:bg-black/10 px-4 py-3 font-semibold text-zinc-800 transition-colors"
				>
					<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 3v4a1 1 0 001 1h4M5 12V5a2 2 0 012-2h7l5 5v11a2 2 0 01-2 2H7a2 2 0 01-2-2v-1" /></svg>
					Build Guide &amp; Models
				</a>
				<a
					href="https://github.com/leafyjerk/PS-250-Remote-Power-Controller"
					target="_blank"
					rel="noopener noreferrer"
					className="inline-flex items-center justify-center gap-2 rounded-xl border border-black/15 bg-black/5 hover:bg-black/10 px-4 py-3 font-semibold text-zinc-800 transition-colors"
				>
					<svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 .5C5.73.5.5 5.73.5 12c0 5.08 3.29 9.39 7.86 10.91.58.11.79-.25.79-.56 0-.27-.01-1-.02-1.96-3.2.7-3.88-1.54-3.88-1.54-.52-1.33-1.28-1.69-1.28-1.69-1.05-.72.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.77 2.7 1.26 3.36.96.1-.75.4-1.26.73-1.55-2.55-.29-5.24-1.28-5.24-5.69 0-1.26.45-2.28 1.19-3.09-.12-.29-.52-1.46.11-3.05 0 0 .97-.31 3.18 1.18a11.1 11.1 0 015.8 0c2.2-1.49 3.17-1.18 3.17-1.18.63 1.59.23 2.76.11 3.05.74.81 1.19 1.83 1.19 3.09 0 4.42-2.69 5.39-5.25 5.68.41.36.78 1.05.78 2.12 0 1.53-.01 2.77-.01 3.15 0 .31.21.68.8.56A11.51 11.51 0 0023.5 12C23.5 5.73 18.27.5 12 .5z" /></svg>
					GitHub Repo
				</a>
			</div>
		</div>
	);
}

// Detailed content for the idea-gen project (BEYOND section)
function IdeaGenContent() {
	return (
		<div className="text-zinc-800">
			<h2 className="text-3xl md:text-4xl font-black mb-6 uppercase tracking-wider">idea-gen — Creativity by Combination</h2>

			<p className="mb-6">idea-gen grew out of a simple observation: that novel ideas often emerge from the deliberate collision of unrelated words. Pairing an adjective with a noun — over and over, with intent — turns out to be a surprisingly effective engine for creativity. That insight became the seed of a dedicated hardware device built around a curated vocabulary of adjectives and nouns.</p>

			<h3 className="text-xl font-bold uppercase tracking-wide mb-3">Iteration One</h3>
			<p className="mb-4">The first iteration was built on a Raspberry Pi and a lightweight weighted algorithm that learned, over the course of a session, which categories a user gravitated toward and which they dismissed — steering them toward combinations they were likely to find compelling. It was a formative project: it sharpened my embedded design skills, taught me to work within tight constraints, and deepened my understanding of 3D printing tolerances and design for manufacture.</p>
			<p className="mb-6">It worked. But it was also larger and clunkier than it needed to be — and that limitation set the agenda for the next version.</p>

			<div className="mb-8">
				<PlainModelViewer className="w-full h-[280px] md:h-[420px] rounded-2xl border border-black/10 bg-black/5 overflow-hidden" fitMultiplier={0.8} modelUrl="/musecubesite.glb" />
				<p className="mt-2 text-sm text-zinc-500">Iteration one. Drag to rotate, scroll to zoom.</p>
			</div>

			<h3 className="text-xl font-bold uppercase tracking-wide mb-3">Iteration Two</h3>
			<p className="mb-4">The second iteration set out to do everything the first did, in a dramatically smaller package. That constraint demanded a far more creative and intentional approach to the mechanical design — and it finally forced me to learn PCB design, which I did from the ground up, sending the boards out for fabrication.</p>
			<p className="mb-4">The hardware is built around an ESP32, a compact OLED display, custom PCBs, physical buttons, an onboard battery paired with a dedicated battery-management module, and a USB-C breakout for charging and data. A more sophisticated recommendation algorithm runs the experience.</p>
			<p className="mb-4">The improved display opened up the user interface. Each word is now presented with its definition and an accompanying image. Because the vocabulary is large, the artwork is produced through a lightweight image-generation pipeline (SDXL-Turbo): each image is generated, converted to grayscale, thresholded, auto-inverted, given an alpha channel, and resized to 64×64 — a process that yields charming, stylized results well suited to the small screen.</p>
			<p className="mb-6">Iteration two was a substantially deeper undertaking than the first, and it stretched me across mechanical, electrical, and software design in ways the original never did.</p>

			<div className="mb-4">
				<PlainModelViewer className="w-full h-[280px] md:h-[420px] rounded-2xl border border-black/10 bg-black/5 overflow-hidden" fitMultiplier={0.8} modelUrl="/tcomb.glb" transparentMaterials={['Material.001']} />
				<p className="mt-2 text-sm text-zinc-500">Iteration two. Drag to rotate, scroll to zoom.</p>
			</div>
		</div>
	);
}

// Modal Component for Works
function ProjectModal({ project, onClose }) {
	if (!project) return null;

	// BEYOND projects (PS-250, idea-gen) use a light panel with dark text to suit the
	// white section; Works projects keep their dark-glass look over the dark sections.
	const isBeyond = project.id === 6 || project.id === 7;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4" onClick={onClose}>
			<div
				className={`relative max-w-2xl w-full max-h-[90vh] md:max-h-[80vh] backdrop-blur-md rounded-2xl border overflow-hidden ${isBeyond ? 'bg-zinc-100/95 border-black/10' : 'bg-zinc-900/85 border-white/20'}`}
				onClick={(e) => e.stopPropagation()}
			>
				<button
					onClick={onClose}
					className={`absolute top-3 right-3 md:top-4 md:right-4 w-8 h-8 flex items-center justify-center rounded-full transition-colors z-10 ${isBeyond ? 'bg-black/10 hover:bg-black/20 text-zinc-800' : 'bg-white/10 hover:bg-white/20 text-white'}`}
					aria-label="Close"
				>
					<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<path d="M18 6L6 18M6 6l12 12" />
					</svg>
				</button>
				<div className="p-4 md:p-6 overflow-y-auto max-h-[90vh] md:max-h-[80vh]">
					{project.id === 4 ? (
						<HallContent />
					) : project.id === 1 ? (
						<MerigoldContent />
					) : project.id === 2 ? (
						<UMMCContent />
					) : project.id === 3 ? (
						<TurcotteContent />
					) : project.id === 5 ? (
						<QuickSpoolContent />
					) : project.id === 6 ? (
						<PS250Content />
					) : project.id === 7 ? (
						<IdeaGenContent />
					) : (
						<>
							<h2 className="text-3xl font-bold mb-4 uppercase tracking-wider">{project.title}</h2>
							<div className="space-y-4 text-white/80 font-light">
								<p>{project.description}</p>
								<p>Detailed content coming soon.</p>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
}

// Modal Component for Services anchored to tile center
function ServiceModal({ service, anchor, onClose }) {
	if (!service || !anchor) return null;

	const modalStyle = {
		top: anchor.y + 'px',
		left: anchor.x + 'px',
		transform: 'translate(-50%, -50%)'
	};

	return (
		<div className="fixed inset-0 z-50" onClick={onClose}>
			<div 
				className="fixed max-w-2xl w-[88vw] md:w-[720px] max-h-[80vh] bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden shadow-2xl"
				style={modalStyle}
				onClick={(e) => e.stopPropagation()}
			>
				<button
					onClick={onClose}
					className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
				>
					<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<path d="M18 6L6 18M6 6l12 12" />
					</svg>
				</button>
				<div className="p-8 overflow-y-auto max-h-[80vh]">
					<h2 className="text-3xl font-bold mb-3 uppercase tracking-wider">{service.title}</h2>
					<p className="text-white/80 mb-5">{service.subtitle}</p>
					<div className="space-y-4 text-white/85 font-light leading-relaxed">
						{service.details.map((para, i) => (
							<p key={i}>{para}</p>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}

// Locked shader tunables
const shaderTunables = {
	c1r: 1, c1g: 0.3, c1b: 0.13,
	c2r: 0.65, c2g: 0.62, c2b: 0,
	c3r: 0.98, c3g: 0.05, c3b: 0,
	whiteBlend: 0,
	whitePow: 5,
	pinkBlend: 0,
	pinkPow: 5,
};

function createTunedMaterial() {
	const t = shaderTunables;
	return new THREE.ShaderMaterial({
		uniforms: {
			uC1: { value: new THREE.Vector3(t.c1r, t.c1g, t.c1b) },
			uC2: { value: new THREE.Vector3(t.c2r, t.c2g, t.c2b) },
			uC3: { value: new THREE.Vector3(t.c3r, t.c3g, t.c3b) },
			uWhiteBlend: { value: t.whiteBlend },
			uWhitePow: { value: t.whitePow },
			uPinkBlend: { value: t.pinkBlend },
			uPinkPow: { value: t.pinkPow },
		},
		vertexShader: `
			varying vec3 vNormal;
			void main() {
				vNormal = normalize(normalMatrix * normal);
				gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
			}
		`,
		fragmentShader: `
			uniform vec3 uC1;
			uniform vec3 uC2;
			uniform vec3 uC3;
			uniform float uWhiteBlend;
			uniform float uWhitePow;
			uniform float uPinkBlend;
			uniform float uPinkPow;
			varying vec3 vNormal;
			void main() {
				vec3 n = normalize(vNormal) * 0.5 + 0.5;
				vec3 color = uC1 * n.x + uC2 * n.y + uC3 * n.z;
				float facing = pow(n.z, uWhitePow);
				color = mix(color, vec3(1.0), facing * uWhiteBlend);
				color = mix(color, uC2, pow(n.y, uPinkPow) * uPinkBlend);
				gl_FragColor = vec4(color, 1.0);
			}
		`,
		side: THREE.DoubleSide,
	});
}

// Track all active materials so debug panel can update them live
const activeMaterials = [];

function applyNormalMaterial(root) {
	const mat = createTunedMaterial();
	activeMaterials.push(mat);
	root.traverse((child) => {
		if (child.isMesh) {
			child.material = mat;
		}
	});
}


// Debug panel component for tuning shader colors
function ShaderDebugPanel() {
	const [, forceUpdate] = useState(0);
	const [open, setOpen] = useState(true);
	const t = shaderTunables;

	const update = (key, val) => {
		shaderTunables[key] = val;
		activeMaterials.forEach((mat) => {
			if (!mat.uniforms) return;
			mat.uniforms.uC1.value.set(t.c1r, t.c1g, t.c1b);
			mat.uniforms.uC2.value.set(t.c2r, t.c2g, t.c2b);
			mat.uniforms.uC3.value.set(t.c3r, t.c3g, t.c3b);
			mat.uniforms.uWhiteBlend.value = t.whiteBlend;
			mat.uniforms.uWhitePow.value = t.whitePow;
			mat.uniforms.uPinkBlend.value = t.pinkBlend;
			mat.uniforms.uPinkPow.value = t.pinkPow;
		});
		forceUpdate((n) => n + 1);
	};

	const slider = (label, key, min, max, step) => (
		<label className="block mb-1" key={key}>
			<span className="text-xs text-white/70">{label} <span className="text-white/50">{t[key].toFixed(2)}</span></span>
			<input type="range" min={min} max={max} step={step} value={t[key]}
				onChange={(e) => update(key, parseFloat(e.target.value))}
				className="w-full h-1" />
		</label>
	);

	return (
		<div className="fixed bottom-4 left-4 z-[100]">
			<button onClick={() => setOpen(!open)}
				className="bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-3 py-1 text-xs backdrop-blur-md mb-2">
				{open ? 'Hide' : 'Shader'} Debug
			</button>
			{open && (
				<div className="bg-black/80 backdrop-blur-md border border-white/20 rounded-xl p-3 w-64 text-white max-h-[80vh] overflow-y-auto">
					<p className="text-xs font-bold uppercase tracking-wide mb-2">Color 1 (X-axis)</p>
					{slider('R', 'c1r', 0, 1, 0.01)}
					{slider('G', 'c1g', 0, 1, 0.01)}
					{slider('B', 'c1b', 0, 1, 0.01)}
					<p className="text-xs font-bold uppercase tracking-wide mb-2 mt-3">Color 2 (Y-axis)</p>
					{slider('R', 'c2r', 0, 1, 0.01)}
					{slider('G', 'c2g', 0, 1, 0.01)}
					{slider('B', 'c2b', 0, 1, 0.01)}
					<p className="text-xs font-bold uppercase tracking-wide mb-2 mt-3">Color 3 (Z-axis)</p>
					{slider('R', 'c3r', 0, 1, 0.01)}
					{slider('G', 'c3g', 0, 1, 0.01)}
					{slider('B', 'c3b', 0, 1, 0.01)}
					<p className="text-xs font-bold uppercase tracking-wide mb-2 mt-3">Effects</p>
					{slider('White Blend', 'whiteBlend', 0, 1, 0.01)}
					{slider('White Sharpness', 'whitePow', 0.5, 5, 0.1)}
					{slider('Pink Blend', 'pinkBlend', 0, 1, 0.01)}
					{slider('Pink Sharpness', 'pinkPow', 0.5, 5, 0.1)}
					<button onClick={() => {
						const out = JSON.stringify(shaderTunables, null, 2);
						navigator.clipboard.writeText(out).catch(() => {});
						alert('Copied to clipboard:\\n' + out);
					}} className="mt-3 w-full bg-white/10 hover:bg-white/20 border border-white/20 rounded px-2 py-1 text-xs">
						Copy Values
					</button>
				</div>
			)}
		</div>
	);
}

// Global ember tunables — adjusted via debug panel
const emberTunables = {
	count: 200,
	sizeMin: 17,
	sizeMax: 1,
	sizeFactor: 520,
	lifetime: 1.3,
	lifetimeRand: 2.3,
	speed: 0.183,
	speedSide: 0.063,
	drag: 0.98,
};

function EmberDebugPanel() {
	const [, forceUpdate] = useState(0);
	const [open, setOpen] = useState(true);
	const t = emberTunables;

	const update = (key, val) => {
		emberTunables[key] = val;
		forceUpdate((n) => n + 1);
	};

	const slider = (label, key, min, max, step) => (
		<label className="block mb-1" key={key}>
			<span className="text-xs text-white/70">{label} <span className="text-white/50">{t[key].toFixed(3)}</span></span>
			<input type="range" min={min} max={max} step={step} value={t[key]}
				onChange={(e) => update(key, parseFloat(e.target.value))}
				className="w-full h-1" />
		</label>
	);

	return (
		<div className="fixed bottom-4 left-4 z-[100]">
			<button onClick={() => setOpen(!open)}
				className="bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-3 py-1 text-xs backdrop-blur-md mb-2">
				{open ? 'Hide' : 'Ember'} Debug
			</button>
			{open && (
				<div className="bg-black/80 backdrop-blur-md border border-white/20 rounded-xl p-3 w-64 text-white max-h-[80vh] overflow-y-auto">
					<p className="text-xs font-bold uppercase tracking-wide mb-2">Particle Size</p>
					{slider('Start Size', 'sizeMin', 1, 30, 0.5)}
					{slider('End Size', 'sizeMax', 0.5, 20, 0.5)}
					{slider('Size Factor (depth)', 'sizeFactor', 50, 1500, 10)}
					<p className="text-xs font-bold uppercase tracking-wide mb-2 mt-3">Lifetime</p>
					{slider('Base Lifetime', 'lifetime', 0.1, 5, 0.1)}
					{slider('Random Extra', 'lifetimeRand', 0, 5, 0.1)}
					<p className="text-xs font-bold uppercase tracking-wide mb-2 mt-3">Motion</p>
					{slider('Up Speed', 'speed', 0.001, 0.2, 0.001)}
					{slider('Side Speed', 'speedSide', 0.001, 0.1, 0.001)}
					{slider('Drag', 'drag', 0.9, 1.0, 0.005)}
					<button onClick={() => {
						const out = JSON.stringify(emberTunables, null, 2);
						navigator.clipboard.writeText(out).catch(() => {});
						alert('Copied to clipboard:\\n' + out);
					}} className="mt-3 w-full bg-white/10 hover:bg-white/20 border border-white/20 rounded px-2 py-1 text-xs">
						Copy Values
					</button>
				</div>
			)}
		</div>
	);
}

// Wireframe + solid hybrid material for engineering aesthetic
function applyWireframeHybridMaterial(root) {
	root.traverse((child) => {
		if (child.isMesh) {
			// Dark solid base
			child.material = new THREE.MeshPhongMaterial({
				color: 0x1a1a2e,
				shininess: 30,
				side: THREE.DoubleSide,
			});
			// Wireframe overlay as a cloned mesh
			const wireGeo = child.geometry.clone();
			const wireMat = new THREE.MeshBasicMaterial({
				color: 0x00ccff,
				wireframe: true,
				transparent: true,
				opacity: 0.35,
				side: THREE.DoubleSide,
			});
			const wireMesh = new THREE.Mesh(wireGeo, wireMat);
			wireMesh.position.copy(child.position);
			wireMesh.rotation.copy(child.rotation);
			wireMesh.scale.copy(child.scale);
			child.parent.add(wireMesh);
		}
	});
}

// Wireframe hybrid viewer for detail modals
function WireframeModelViewer({ className, fitMultiplier = 0.8, modelUrl = '/gun.glb' }) {
	const containerRef = useRef(null);
	const frameRef = useRef(0);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const scene = new THREE.Scene();
		scene.background = null;

		const width = container.clientWidth;
		const height = container.clientHeight;
		const camera = new THREE.PerspectiveCamera(45, width / height, 0.01, 10000);

		const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, logarithmicDepthBuffer: true });
		renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
		renderer.setSize(width, height);
		renderer.setClearColor(0x000000, 0);
		container.appendChild(renderer.domElement);

		scene.add(new THREE.AmbientLight(0xffffff, 0.5));
		const dir1 = new THREE.DirectionalLight(0xffffff, 1.0);
		dir1.position.set(3, 4, 2);
		scene.add(dir1);
		const dir2 = new THREE.DirectionalLight(0x4488ff, 0.4);
		dir2.position.set(-3, 2, -2);
		scene.add(dir2);

		const controls = new ThreeOrbitControls(camera, renderer.domElement);
		controls.enablePan = false;
		controls.autoRotate = true;
		controls.autoRotateSpeed = 0.8;
		try {
			if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
				controls.enabled = false;
				renderer.domElement.style.touchAction = 'auto';
			}
		} catch (_) {}
		controls.addEventListener('start', () => (controls.autoRotate = false));
		controls.addEventListener('end', () => (controls.autoRotate = true));

		const loader = new GLTFLoader();
		loader.load(modelUrl, (gltf) => {
			const root = gltf.scene || gltf.scenes[0];
			scene.add(root);
			applyWireframeHybridMaterial(root);

			const box = new THREE.Box3().setFromObject(root);
			const center = box.getCenter(new THREE.Vector3());
			const sphere = box.getBoundingSphere(new THREE.Sphere());
			const radius = Math.max(0.0001, sphere.radius);
			const fov = (camera.fov * Math.PI) / 180;
			const distance = (radius / Math.sin(fov / 2)) * fitMultiplier;
			controls.target.copy(center);
			camera.near = Math.max(0.001, distance / 1000);
			camera.far = Math.max(100, distance * 100);
			camera.updateProjectionMatrix();
			camera.position.copy(center.clone().add(new THREE.Vector3(distance, distance * 0.4, distance * 1.1)));
			camera.lookAt(center);
			controls.minDistance = Math.max(0.001, distance * 0.1);
			controls.maxDistance = distance * 4;
		}, undefined, () => {});

		const onResize = () => {
			const w = container.clientWidth;
			const h = container.clientHeight;
			renderer.setSize(w, h);
			camera.aspect = w / h;
			camera.updateProjectionMatrix();
		};
		window.addEventListener('resize', onResize);

		const animate = () => {
			frameRef.current = requestAnimationFrame(animate);
			controls.update();
			renderer.render(scene, camera);
		};
		animate();

		return () => {
			cancelAnimationFrame(frameRef.current);
			window.removeEventListener('resize', onResize);
			controls.dispose();
			renderer.dispose();
			if (renderer.domElement && renderer.domElement.parentNode) {
				renderer.domElement.parentNode.removeChild(renderer.domElement);
			}
		};
	}, [modelUrl, fitMultiplier]);

	return <div ref={containerRef} className={className || "w-full h-[420px] rounded-2xl border border-white/20 bg-white/5 overflow-hidden"} />;
}

// Plain GLTF viewer that PRESERVES the model's original (Blender-exported) materials.
// Used by the BEYOND section. Orbit + zoom + auto-rotate; no shader override, no embers.
// Click-vs-drag is distinguished by pointer travel so rotating doesn't trigger onClick.
function PlainModelViewer({ className, onClick, fitMultiplier = 0.8, modelUrl, transparentMaterials = [] }) {
	const containerRef = useRef(null);
	const frameRef = useRef(0);
	const pointerStart = useRef(null);

	const handlePointerDown = (e) => {
		pointerStart.current = { x: e.clientX, y: e.clientY };
	};
	const handlePointerUp = (e) => {
		if (!pointerStart.current || !onClick) return;
		const dx = e.clientX - pointerStart.current.x;
		const dy = e.clientY - pointerStart.current.y;
		if (Math.abs(dx) < 5 && Math.abs(dy) < 5) onClick();
		pointerStart.current = null;
	};

	useEffect(() => {
		const container = containerRef.current;
		if (!container || !modelUrl) return;

		const scene = new THREE.Scene();
		scene.background = null;

		const width = container.clientWidth;
		const height = container.clientHeight;
		const camera = new THREE.PerspectiveCamera(45, width / height, 0.01, 10000);

		const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, logarithmicDepthBuffer: true });
		renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
		renderer.setSize(width, height);
		renderer.setClearColor(0x000000, 0);
		// Color-accurate rendering to keep materials close to the Blender export
		try { renderer.outputEncoding = THREE.sRGBEncoding; } catch (_) { try { renderer.outputColorSpace = THREE.SRGBColorSpace; } catch (_) {} }
		try { renderer.toneMapping = THREE.ACESFilmicToneMapping; } catch (_) {}
		renderer.toneMappingExposure = 1.02;
		container.appendChild(renderer.domElement);

		// Neutral lighting so original materials read accurately
		scene.add(new THREE.AmbientLight(0xffffff, 0.6));
		const hemi = new THREE.HemisphereLight(0xffffff, 0x444444, 0.5);
		scene.add(hemi);
		const dir1 = new THREE.DirectionalLight(0xffffff, 1.1);
		dir1.position.set(3, 4, 2);
		scene.add(dir1);
		const dir2 = new THREE.DirectionalLight(0xffffff, 0.5);
		dir2.position.set(-3, 2, -2);
		scene.add(dir2);

		const controls = new ThreeOrbitControls(camera, renderer.domElement);
		controls.enablePan = false;
		controls.autoRotate = true;
		controls.autoRotateSpeed = 0.8;
		try {
			if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
				controls.enabled = false;
				renderer.domElement.style.touchAction = 'auto';
			}
		} catch (_) {}
		controls.addEventListener('start', () => (controls.autoRotate = false));
		controls.addEventListener('end', () => (controls.autoRotate = true));

		let mixer = null;
		const loader = new GLTFLoader();
		loader.load(modelUrl, (gltf) => {
			const root = gltf.scene || gltf.scenes[0];
			scene.add(root);
			// Materials are intentionally left exactly as exported, except any
			// explicitly flagged as transparent (by material name) below.
			if (transparentMaterials.length > 0) {
				root.traverse((child) => {
					if (!child.isMesh || !child.material) return;
					const mats = Array.isArray(child.material) ? child.material : [child.material];
					mats.forEach((m) => {
						if (m && transparentMaterials.includes(m.name)) {
							m.transparent = true;
							m.opacity = m.opacity < 1 ? m.opacity : 0.25;
							m.depthWrite = false;
							m.needsUpdate = true;
						}
					});
				});
			}

			const box = new THREE.Box3().setFromObject(root);
			const center = box.getCenter(new THREE.Vector3());
			const sphere = box.getBoundingSphere(new THREE.Sphere());
			const radius = Math.max(0.0001, sphere.radius);
			const fov = (camera.fov * Math.PI) / 180;
			const distance = (radius / Math.sin(fov / 2)) * fitMultiplier;
			controls.target.copy(center);
			camera.near = Math.max(0.001, distance / 1000);
			camera.far = Math.max(100, distance * 100);
			camera.updateProjectionMatrix();
			camera.position.copy(center.clone().add(new THREE.Vector3(distance, distance * 0.5, distance * 1.1)));
			camera.lookAt(center);
			controls.minDistance = Math.max(0.001, distance * 0.1);
			controls.maxDistance = distance * 4;

			if (gltf.animations && gltf.animations.length > 0) {
				mixer = new THREE.AnimationMixer(root);
				gltf.animations.forEach((clip) => mixer.clipAction(clip).reset().play());
			}
		}, undefined, () => {});

		const onResize = () => {
			const w = container.clientWidth;
			const h = container.clientHeight;
			renderer.setSize(w, h);
			camera.aspect = w / h;
			camera.updateProjectionMatrix();
		};
		window.addEventListener('resize', onResize);

		const animate = () => {
			frameRef.current = requestAnimationFrame(animate);
			controls.update();
			if (mixer) mixer.update(0.016);
			renderer.render(scene, camera);
		};
		animate();

		return () => {
			cancelAnimationFrame(frameRef.current);
			window.removeEventListener('resize', onResize);
			controls.dispose();
			renderer.dispose();
			if (renderer.domElement && renderer.domElement.parentNode) {
				renderer.domElement.parentNode.removeChild(renderer.domElement);
			}
		};
	}, [modelUrl, fitMultiplier]);

	return <div ref={containerRef} onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} className={className || "w-full h-[420px] rounded-2xl border border-white/20 bg-white/5 overflow-hidden"} />;
}

// 3D Marigold viewer using plain three.js (no react-three dependencies)
// Shared sync bus so every Works MarigoldViewer starts auto-rotate (and any
// built-in GLB animations) on the same frame, once every viewer has loaded.
const worksSyncBus = {
	participants: new Map(),
	hasStarted: false,
	join(id, onStart) {
		this.participants.set(id, { loaded: false, onStart });
		if (this.hasStarted) onStart();
		return () => {
			this.participants.delete(id);
		};
	},
	reportLoaded(id) {
		if (this.hasStarted) return;
		const entry = this.participants.get(id);
		if (!entry) return;
		entry.loaded = true;
		let allLoaded = this.participants.size > 0;
		this.participants.forEach((v) => { if (!v.loaded) allLoaded = false; });
		if (allLoaded) {
			this.hasStarted = true;
			this.participants.forEach((v) => v.onStart());
		}
	}
};

function MarigoldViewer({ className, onClick, fitMultiplier = 0.8, modelUrl = '/merigold.gltf' }) {
	const containerRef = useRef(null);
	const rendererRef = useRef(null);
	const controlsRef = useRef(null);
	const frameRef = useRef(0);
	const pointerStart = useRef(null);
	const emberState = useRef(null);

	const handlePointerDown = (e) => {
		pointerStart.current = { x: e.clientX, y: e.clientY };
	};
	const handlePointerUp = (e) => {
		if (!pointerStart.current || !onClick) return;
		const dx = e.clientX - pointerStart.current.x;
		const dy = e.clientY - pointerStart.current.y;
		if (Math.abs(dx) < 5 && Math.abs(dy) < 5) {
			onClick();
		}
		pointerStart.current = null;
	};

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const syncId = Symbol('worksViewer');
		let hasStarted = false;
		let mixer = null;

		const scene = new THREE.Scene();
		scene.background = null;

		const width = container.clientWidth;
		const height = container.clientHeight;
		const camera = new THREE.PerspectiveCamera(45, width / height, 0.01, 10000);
		camera.position.set(0.6, 0.5, 2.2);

		const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, logarithmicDepthBuffer: true });
		renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
		renderer.setSize(width, height);
		rendererRef.current = renderer;
		container.appendChild(renderer.domElement);

		// Lights
		scene.add(new THREE.AmbientLight(0xffffff, 0.35));
		const dir1 = new THREE.DirectionalLight(0xffffff, 1.0);
		dir1.position.set(3, 4, 2);
		scene.add(dir1);
		const dir2 = new THREE.DirectionalLight(0xffffff, 0.4);
		dir2.position.set(-3, 2, -2);
		scene.add(dir2);

		// Controls
		const controls = new ThreeOrbitControls(camera, renderer.domElement);
		controls.enablePan = false;
		controls.autoRotate = false; // gated by worksSyncBus until every viewer loads
		controls.autoRotateSpeed = 0.6;
		// Disable orbit on touch-only devices to avoid scroll conflicts
		try {
			if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
				controls.enabled = false;
				renderer.domElement.style.touchAction = 'auto';
			}
		} catch (_) {}
		controlsRef.current = controls;
		controls.addEventListener('start', () => (controls.autoRotate = false));
		controls.addEventListener('end', () => { if (hasStarted) controls.autoRotate = true; });

		// Wait for every Works viewer to load, then start rotation + any GLB animations together
		const leaveSync = worksSyncBus.join(syncId, () => {
			hasStarted = true;
			controls.autoRotate = true;
		});

		// Load GLTF (served from public root)
		const loader = new GLTFLoader();
		loader.load(
			modelUrl,
			(gltf) => {
				const root = gltf.scene || gltf.scenes[0];
				scene.add(root);

				// Apply normal material for iridescent look
				applyNormalMaterial(root);

				// Frame the model with robust near/far and distance
				const box = new THREE.Box3().setFromObject(root);
				const center = box.getCenter(new THREE.Vector3());
				const sphere = box.getBoundingSphere(new THREE.Sphere());
				const radius = Math.max(0.0001, sphere.radius);
				const fov = (camera.fov * Math.PI) / 180;
				const distance = (radius / Math.sin(fov / 2)) * fitMultiplier;
				controls.target.copy(center);
				camera.near = Math.max(0.001, distance / 1000);
				camera.far = Math.max(100, distance * 100);
				camera.updateProjectionMatrix();
				camera.position.copy(center.clone().add(new THREE.Vector3(distance, distance * 0.6, distance * 1.1)));
				camera.lookAt(center);
				controls.minDistance = Math.max(0.001, distance * 0.1);
				controls.maxDistance = distance * 4;

				// --- Ember particle system ---
				const surfaceVerts = [];
				root.traverse((child) => {
					if (child.isMesh && child.geometry) {
						const pos = child.geometry.attributes.position;
						if (!pos) return;
						child.updateMatrixWorld();
						for (let i = 0; i < pos.count; i += 3) {
							const v = new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i));
							v.applyMatrix4(child.matrixWorld);
							surfaceVerts.push(v);
						}
					}
				});

				if (surfaceVerts.length > 0) {
					const PARTICLE_COUNT = 200;
					const positions = new Float32Array(PARTICLE_COUNT * 3);
					const velocities = new Float32Array(PARTICLE_COUNT * 3);
					const lifetimes = new Float32Array(PARTICLE_COUNT);
					const ages = new Float32Array(PARTICLE_COUNT);

					const spawnParticle = (i) => {
						const src = surfaceVerts[Math.floor(Math.random() * surfaceVerts.length)];
						positions[i * 3] = src.x;
						positions[i * 3 + 1] = src.y;
						positions[i * 3 + 2] = src.z;
						velocities[i * 3] = (Math.random() - 0.5) * radius * emberTunables.speedSide;
						velocities[i * 3 + 1] = Math.random() * radius * emberTunables.speed + radius * 0.005;
						velocities[i * 3 + 2] = (Math.random() - 0.5) * radius * emberTunables.speedSide;
						lifetimes[i] = emberTunables.lifetime + Math.random() * emberTunables.lifetimeRand;
						ages[i] = 0;
					};

					for (let i = 0; i < PARTICLE_COUNT; i++) {
						spawnParticle(i);
						ages[i] = Math.random() * lifetimes[i]; // stagger initial ages
					}

					const geo = new THREE.BufferGeometry();
					geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
					geo.setAttribute('aLife', new THREE.BufferAttribute(new Float32Array(PARTICLE_COUNT), 1));

					const particleMat = new THREE.ShaderMaterial({
						uniforms: {
							uSizeStart: { value: emberTunables.sizeMin },
							uSizeEnd: { value: emberTunables.sizeMax },
							uSizeFactor: { value: emberTunables.sizeFactor },
						},
						vertexShader: `
							uniform float uSizeStart;
							uniform float uSizeEnd;
							uniform float uSizeFactor;
							attribute float aLife;
							varying float vLife;
							void main() {
								vLife = aLife;
								vec4 mvPos = modelViewMatrix * vec4(position, 1.0);
								gl_PointSize = mix(uSizeEnd, uSizeStart, aLife) * (uSizeFactor / -mvPos.z);
								gl_Position = projectionMatrix * mvPos;
							}
						`,
						fragmentShader: `
							varying float vLife;
							void main() {
								float d = length(gl_PointCoord - 0.5) * 2.0;
								if (d > 1.0) discard;
								float glow = pow(1.0 - d, 2.0);
								float alpha = vLife * glow;
								vec3 color = mix(vec3(1.0, 0.3, 0.0), vec3(1.0, 0.85, 0.3), vLife);
								gl_FragColor = vec4(color, alpha);
							}
						`,
						transparent: true,
						blending: THREE.AdditiveBlending,
						depthWrite: false,
					});

					const particles = new THREE.Points(geo, particleMat);
					scene.add(particles);

					// Store refs for animate loop
					emberState.current = { positions, velocities, lifetimes, ages, geo, surfaceVerts, radius, PARTICLE_COUNT, spawnParticle, particleMat };
				}

				// Wire up built-in GLB animations (e.g., QuickSpool's clips)
				if (gltf.animations && gltf.animations.length > 0) {
					mixer = new THREE.AnimationMixer(root);
					gltf.animations.forEach((clip) => {
						const action = mixer.clipAction(clip);
						action.reset();
						action.play();
					});
				}

				worksSyncBus.reportLoaded(syncId);
			},
			undefined,
			() => {
				// Still report so a missing file doesn't block sync forever
				worksSyncBus.reportLoaded(syncId);
			}
		);

		// Resize handler
		const onResize = () => {
			const w = container.clientWidth;
			const h = container.clientHeight;
			renderer.setSize(w, h);
			camera.aspect = w / h;
			camera.updateProjectionMatrix();
		};
		window.addEventListener('resize', onResize);

		// Animate
		const animate = () => {
			frameRef.current = requestAnimationFrame(animate);
			controls.update();

			// Advance built-in GLB animations only after sync fires
			if (mixer && hasStarted) {
				mixer.update(0.016);
			}

			// Update ember particles
			const e = emberState.current;
			if (e) {
				const dt = 0.016;
				const lifeAttr = e.geo.getAttribute('aLife');
				// Update shader uniforms from tunables
				e.particleMat.uniforms.uSizeStart.value = emberTunables.sizeMin;
				e.particleMat.uniforms.uSizeEnd.value = emberTunables.sizeMax;
				e.particleMat.uniforms.uSizeFactor.value = emberTunables.sizeFactor;
				for (let i = 0; i < e.PARTICLE_COUNT; i++) {
					e.ages[i] += dt;
					if (e.ages[i] >= e.lifetimes[i]) {
						e.spawnParticle(i);
					}
					e.positions[i * 3] += e.velocities[i * 3] * dt;
					e.positions[i * 3 + 1] += e.velocities[i * 3 + 1] * dt;
					e.positions[i * 3 + 2] += e.velocities[i * 3 + 2] * dt;
					// Decelerate
					e.velocities[i * 3] *= emberTunables.drag;
					e.velocities[i * 3 + 1] *= emberTunables.drag;
					e.velocities[i * 3 + 2] *= emberTunables.drag;
					const life = 1.0 - Math.min(1, e.ages[i] / e.lifetimes[i]);
					lifeAttr.array[i] = life;
				}
				e.geo.attributes.position.needsUpdate = true;
				lifeAttr.needsUpdate = true;
			}

			renderer.render(scene, camera);
		};
		animate();

		return () => {
			leaveSync();
			cancelAnimationFrame(frameRef.current);
			window.removeEventListener('resize', onResize);
			controls.dispose();
			renderer.dispose();
			if (renderer.domElement && renderer.domElement.parentNode) {
				renderer.domElement.parentNode.removeChild(renderer.domElement);
			}
		};
	}, []);

	return (
		<div ref={containerRef} onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} className={className || "w-full h-[420px] rounded-2xl border border-white/20 bg-white/5 overflow-hidden"} />
	);
}

function PointsUniverse({ theme = "fever", densityScale = 2.5, showLogo = false, palette, gradientColors, hueDrift = 10, lightMode = false }) {
	const canvasRef = useRef(null);
	const rafRef = useRef(0);
	const dprRef = useRef(1);
	const mouse = useRef({ x: -9999, y: -9999, down: false, hover: false });
	const topoRef = useRef({ canvas: null, ctx: null, w: 0, h: 0, imageData: null });
	const currentPalette = useRef(palette || ["#ffb347", "#ff7f50", "#ff6347", "#ff4500", "#ffa500"]);
	const targetPalette = useRef(palette || ["#ffb347", "#ff7f50", "#ff6347", "#ff4500", "#ffa500"]);
	const currentGradient = useRef(gradientColors || { start: { h: 15, s: 90, l: 50 }, end: { h: 30, s: 90, l: 60 } });
	const targetGradient = useRef(gradientColors || { start: { h: 15, s: 90, l: 50 }, end: { h: 30, s: 90, l: 60 } });
	const transitionProgress = useRef(1);
	// Light sections (e.g. BEYOND) need normal-blend dots/lines so blue/black dots
	// and grey topo lines are visible on a white background. Kept in a ref so the
	// render loop reads it live without re-initializing the canvas.
	const lightModeRef = useRef(lightMode);
	useEffect(() => { lightModeRef.current = lightMode; }, [lightMode]);

	// Update target colors when props change
	useEffect(() => {
		if (palette) {
			targetPalette.current = palette;
			transitionProgress.current = 0;
			if (!currentPalette.current || currentPalette.current.length === 0) {
				currentPalette.current = [...palette];
			}
		}
		if (gradientColors) {
			targetGradient.current = gradientColors;
			transitionProgress.current = 0;
			if (!currentGradient.current || !currentGradient.current.start) {
				currentGradient.current = { ...gradientColors };
			}
		}
		// If hueDrift is zero (locked), snap instantly to target to avoid off-hue returns
		if (hueDrift === 0) {
			transitionProgress.current = 1;
			currentPalette.current = [...(targetPalette.current || currentPalette.current)];
			currentGradient.current = { ...(targetGradient.current || currentGradient.current) };
		}
	}, [palette, gradientColors, hueDrift]);

	// Helper function to interpolate colors
	const lerpColor = (color1, color2, t) => {
		const r1 = parseInt(color1.slice(1, 3), 16);
		const g1 = parseInt(color1.slice(3, 5), 16);
		const b1 = parseInt(color1.slice(5, 7), 16);
		const r2 = parseInt(color2.slice(1, 3), 16);
		const g2 = parseInt(color2.slice(3, 5), 16);
		const b2 = parseInt(color2.slice(5, 7), 16);
		
		const r = Math.round(r1 + (r2 - r1) * t);
		const g = Math.round(g1 + (g2 - g1) * t);
		const b = Math.round(b1 + (b2 - b1) * t);
		
		return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
	};

	const lerpHSL = (hsl1, hsl2, t) => {
		return {
			h: hsl1.h + (hsl2.h - hsl1.h) * t,
			s: hsl1.s + (hsl2.s - hsl1.s) * t,
			l: hsl1.l + (hsl2.l - hsl1.l) * t
		};
	};

	// Smooth symmetric easing for palette transitions
	const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

	useEffect(() => {
		const canvas = canvasRef.current;
		const ctx = canvas.getContext("2d");
		const state = { t: 0, frame: 0, bg: [], title: [], titleBounds: { x: 0, y: 0, w: 0, h: 0 } };

		const onMove = (e) => {
			const rect = canvas.getBoundingClientRect();
			const cssX = e.clientX - rect.left;
			const cssY = e.clientY - rect.top;
			const dpr = dprRef.current;
			mouse.current.x = cssX * dpr;
			mouse.current.y = cssY * dpr;
			const { x, y, w, h } = state.titleBounds;
			mouse.current.hover =
				mouse.current.x >= x &&
				mouse.current.x <= x + w &&
				mouse.current.y >= y &&
				mouse.current.y <= y + h;
		};
		const onDown = () => (mouse.current.down = true);
		const onUp = () => (mouse.current.down = false);

		const makeTopoCanvas = (W, H) => {
			const scale = 2.5;
			const tw = Math.max(200, Math.floor(W / (10 / scale)));
			const th = Math.max(120, Math.floor(H / (10 / scale)));
			const tCan = document.createElement("canvas");
			tCan.width = tw;
			tCan.height = th;
			const tCtx = tCan.getContext("2d", { willReadFrequently: true });
			topoRef.current.canvas = tCan;
			topoRef.current.ctx = tCtx;
			topoRef.current.w = tw;
			topoRef.current.h = th;
			topoRef.current.imageData = tCtx.createImageData(tw, th);
		};

		const resize = () => {
			const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
			dprRef.current = dpr;
			const w = window.innerWidth;
			const h = window.innerHeight;
			canvas.width = Math.floor(w * dpr);
			canvas.height = Math.floor(h * dpr);
			canvas.style.width = w + "px";
			canvas.style.height = h + "px";
			makeTopoCanvas(canvas.width, canvas.height);
			initBG();
			initTitle();
		};

		const initBG = () => {
			const { width: W, height: H } = canvas;
			const density = 20000 / densityScale;
			const count = Math.floor((W * H) / density);
			state.bg = new Array(count).fill(0).map((_, i) => ({
				x: Math.random() * W,
				y: Math.random() * H,
				vx: (Math.random() - 0.5) * 0.5,
				vy: (Math.random() - 0.5) * 0.5,
				s: 2 + Math.random() * 3,
				c: targetPalette.current[i % targetPalette.current.length],
			}));
		};

		const initTitle = async () => {
			if (!showLogo) {
				state.title = [];
				return;
			}
			const { width: W, height: H } = canvas;
			const off = document.createElement("canvas");
			off.width = Math.floor(W);
			off.height = Math.floor(H);
			const octx = off.getContext("2d");
			octx.clearRect(0, 0, off.width, off.height);

			// Draw POINTS text with variable width font similar to SuperSolid
				const title = "POINTS";
			const fontSize = Math.min(W * 0.6, H * 0.25);
				octx.fillStyle = "#fff";
			
			// Using a condensed, variable-width font stack
			octx.font = `900 ${fontSize}px 'Helvetica Neue', 'Arial Black', system-ui, -apple-system, sans-serif`;
				octx.textAlign = "center";
				octx.textBaseline = "middle";
			
			// Apply letter spacing and transform for SuperSolid-like effect
			octx.save();
			octx.translate(off.width / 2, off.height / 2.5);
			octx.scale(1.3, 1); // Horizontal stretch for wide look
			octx.fillText(title, 0, 0);
			octx.restore();

			const data = octx.getImageData(0, 0, off.width, off.height);
			const step = Math.max(2, Math.floor(Math.sqrt((off.width * off.height) / 15500)));
			const pts = [];
			let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

			for (let y = 0; y < data.height; y += step) {
				for (let x = 0; x < data.width; x += step) {
					if (data.data[(y * data.width + x) * 4 + 3] > 128) {
						const bx = x;
						const by = y;
						minX = Math.min(minX, bx);
						minY = Math.min(minY, by);
						maxX = Math.max(maxX, bx);
						maxY = Math.max(maxY, by);
						pts.push({ x: bx, y: by, bx, by, vx: 0, vy: 0, s: Math.max(2.2, step * 0.65) });
					}
				}
			}

			state.title = pts;
			state.titleBounds = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
		};

		const drawTopo = (W, H, t) => {
			const { canvas: tCan, ctx: tctx, w: TW, h: TH } = topoRef.current;
			if (!tCan) return;

			const img = topoRef.current.imageData;
			const data = img.data;
			
			// More chaotic topographical lines
			const bands = 12; // More elevation bands for topo map look
			const width = 0.018; // Thinner lines for topo map aesthetic
			
			// Multiple noise frequencies for more realistic terrain
			const s1 = 0.025, s2 = 0.038, s3 = 0.072, s4 = 0.015, s5 = 0.095;
			const p1 = t * 0.15, p2 = -t * 0.12, p3 = t * 0.08, p4 = t * 0.22, p5 = -t * 0.18;

			// White lines for dark sections; grey lines for light (BEYOND) sections
			const lineCol = lightModeRef.current ? 90 : 255;

			let idx = 0;
			for (let y = 0; y < TH; y++) {
				for (let x = 0; x < TW; x++) {
					const nx = x / TW, ny = y / TH;
					
					// Layered noise for realistic terrain elevation
					const v = 
						Math.sin(nx / s1 + p1) * 1.2 +
						Math.sin((ny + nx * 0.5) / s2 + p2) * 0.8 +
						Math.sin((nx - ny * 0.3) / s3 + p3) * 0.6 +
						Math.sin((nx * 2.1 + ny * 1.3) / s4 + p4) * 0.4 +
						Math.sin((nx * 0.7 - ny * 1.8) / s5 + p5) * 0.3;
					
					const n = (v + 3.3) / 6.6;
					const g = n * bands;
					const dist = Math.abs(g - Math.round(g));
					const isLine = dist < width;
					
					// Vary line intensity based on elevation
					const elevation = Math.round(g);
					const intensity = isLine ? (60 + elevation * 8) : 0;
					
					data[idx++] = lineCol;
					data[idx++] = lineCol;
					data[idx++] = lineCol;
					data[idx++] = intensity;
				}
			}
			tctx.putImageData(img, 0, 0);

			ctx.save();
			if (lightModeRef.current) {
				// overlay/white is invisible on white — draw grey lines straight over
				ctx.globalCompositeOperation = "source-over";
				ctx.globalAlpha = 0.45;
			} else {
				ctx.globalCompositeOperation = "overlay";
				ctx.globalAlpha = 0.25;
			}
			ctx.imageSmoothingEnabled = false;
			ctx.drawImage(tCan, 0, 0, TW, TH, 0, 0, W, H);
			ctx.restore();
		};

		const stepFrame = () => {
			state.t += 0.016;
			state.frame++;
			const { width: W, height: H } = canvas;
			const dpr = dprRef.current;
			ctx.clearRect(0, 0, W, H);

			// Slower, smoother color transition with ease-in-out
			if (transitionProgress.current < 1) {
				transitionProgress.current = Math.min(1, transitionProgress.current + 0.02);
				const te = easeInOutCubic(transitionProgress.current);
				const step = 0.06 * te; // smaller per-frame blend for slower change
				for (let i = 0; i < targetPalette.current.length; i++) {
					currentPalette.current[i] = lerpColor(
						currentPalette.current[i],
						targetPalette.current[i],
						step
					);
				}
				currentGradient.current = {
					start: lerpHSL(currentGradient.current.start, targetGradient.current.start, step),
					end: lerpHSL(currentGradient.current.end, targetGradient.current.end, step)
				};
				// Snap exactly to target on completion — the incremental lerp otherwise
				// freezes a little short (e.g. white settling as grey).
				if (transitionProgress.current >= 1) {
					for (let i = 0; i < targetPalette.current.length; i++) {
						currentPalette.current[i] = targetPalette.current[i];
					}
					currentGradient.current = {
						start: { ...targetGradient.current.start },
						end: { ...targetGradient.current.end }
					};
				}
			}

			// Always update particle colors to match current palette
			for (const p of state.bg) {
				const idx = state.bg.indexOf(p);
				p.c = currentPalette.current[idx % currentPalette.current.length];
			}

			const startColor = currentGradient.current.start;
			const endColor = currentGradient.current.end;
			const hueOffset = Math.sin(state.t * 0.5) * (hueDrift || 0);
			
			const grad = ctx.createLinearGradient(0, 0, W, H);
			grad.addColorStop(0, `hsl(${startColor.h + hueOffset}, ${startColor.s}%, ${startColor.l}%)`);
			grad.addColorStop(1, `hsl(${endColor.h + hueOffset}, ${endColor.s}%, ${endColor.l}%)`);
			ctx.fillStyle = grad;
			ctx.fillRect(0, 0, W, H);

			// Xbox 360 blades-style radial effect - thicker and more pixelated
			const numBlades = 6;
			const bladeAngle = (TAU / numBlades);
			const pulseOffset = Math.sin(state.t * 0.8) * 0.15;
			
			ctx.save();
			ctx.translate(W/2, H/2);
			ctx.globalCompositeOperation = "overlay";
			ctx.globalAlpha = 0.2;
			
			for (let i = 0; i < numBlades; i++) {
				const angle = i * bladeAngle + state.t * 0.2;
				const gradient = ctx.createLinearGradient(
					0, 0,
					Math.cos(angle) * W, Math.sin(angle) * W
				);
				
				gradient.addColorStop(0, "rgba(255,255,255,0)");
				gradient.addColorStop(0.3 + pulseOffset, "rgba(255,255,255,0.3)");
				gradient.addColorStop(0.5 + pulseOffset, "rgba(255,255,255,0.5)");
				gradient.addColorStop(0.7 + pulseOffset, "rgba(255,255,255,0.3)");
				gradient.addColorStop(1, "rgba(255,255,255,0)");
				
				ctx.fillStyle = gradient;
				ctx.beginPath();
				ctx.moveTo(0, 0);
				ctx.arc(0, 0, Math.max(W, H), angle - bladeAngle/2, angle + bladeAngle/2);
				ctx.closePath();
				ctx.fill();
			}
			
			ctx.restore();
			
			// Add pixelated overlay effect
			ctx.save();
			ctx.imageSmoothingEnabled = false;
			const pixelSize = 3;
			const tempCanvas = document.createElement('canvas');
			tempCanvas.width = Math.floor(W / pixelSize);
			tempCanvas.height = Math.floor(H / pixelSize);
			const tempCtx = tempCanvas.getContext('2d');
			tempCtx.drawImage(canvas, 0, 0, tempCanvas.width, tempCanvas.height);
			ctx.globalAlpha = 0.1;
			ctx.globalCompositeOperation = "overlay";
			ctx.drawImage(tempCanvas, 0, 0, W, H);
			ctx.restore();

			// Recompute topo every other frame to reduce work
			if ((state.frame & 1) === 0) {
				drawTopo(W, H, state.t);
			} else {
				const { canvas: tCan, w: TW, h: TH } = topoRef.current;
				if (tCan) {
					ctx.save();
					if (lightModeRef.current) {
						ctx.globalCompositeOperation = "source-over";
						ctx.globalAlpha = 0.45;
					} else {
						ctx.globalCompositeOperation = "overlay";
						ctx.globalAlpha = 0.35;
					}
					ctx.imageSmoothingEnabled = false;
					ctx.drawImage(tCan, 0, 0, TW, TH, 0, 0, W, H);
					ctx.restore();
				}
			}

			// Additive blend glows on dark bg; on white it'd vanish, so use normal blend
			ctx.globalCompositeOperation = lightModeRef.current ? "source-over" : "lighter";
			for (const p of state.bg) {
				p.x += p.vx;
				p.y += p.vy;
				if (p.x < 0 || p.x > W) p.vx *= -1;
				if (p.y < 0 || p.y > H) p.vy *= -1;
				const size = p.s * dpr;
				ctx.fillStyle = p.c;
				ctx.fillRect(p.x - size * 0.5, p.y - size * 0.5, size, size);
			}

			const influence = mouse.current.hover ? (mouse.current.down ? 480 : 320) * dpr : 0;
			const strengthHover = 4.6;
			const strengthDown = strengthHover * 8;
			const strength = mouse.current.hover ? (mouse.current.down ? strengthDown : strengthHover) : 0;
			const mx = mouse.current.x, my = mouse.current.y;

			for (const p of state.title) {
				if (influence > 0) {
					const dx = mx - p.x;
					const dy = my - p.y;
					const d = Math.hypot(dx, dy) + 0.0001;
					if (d < influence) {
						const f = (strength / d) * 2.0;
						p.vx -= (dx / d) * f;
						p.vy -= (dy / d) * f;
					}
				}
				p.vx += (p.bx - p.x) * 0.012;
				p.vy += (p.by - p.y) * 0.012;
				p.vx *= 0.92;
				p.vy *= 0.92;
				p.x += p.vx;
				p.y += p.vy;
			}

			if (showLogo) {
			ctx.globalCompositeOperation = "screen";
			for (const p of state.title) {
				const r = (p.s + 0.8) * dpr;
				const k = 2.2;
				const sep = 1.5 * dpr;
				const ox = -p.vx * k;
				const oy = -p.vy * k;

				ctx.fillStyle = "rgba(255,0,0,0.8)";
				ctx.beginPath();
				ctx.arc(p.x + ox - sep, p.y + oy, r, 0, TAU);
				ctx.fill();

				ctx.fillStyle = "rgba(0,255,0,0.8)";
				ctx.beginPath();
				ctx.arc(p.x + ox, p.y + oy, r, 0, TAU);
				ctx.fill();

				ctx.fillStyle = "rgba(0,0,255,0.8)";
				ctx.beginPath();
				ctx.arc(p.x + ox + sep, p.y + oy, r, 0, TAU);
				ctx.fill();
			}

			ctx.globalCompositeOperation = "source-over";
			ctx.fillStyle = "rgba(255,255,255,0.96)";
			for (const p of state.title) {
				ctx.beginPath();
				ctx.arc(p.x, p.y, (p.s + 0.8) * dpr, 0, TAU);
				ctx.fill();
				}
			}

			rafRef.current = requestAnimationFrame(stepFrame);
		};

		resize();
		window.addEventListener("resize", resize);
		window.addEventListener("pointermove", onMove, { passive: true });
		window.addEventListener("pointerdown", onDown, { passive: true });
		window.addEventListener("pointerup", onUp, { passive: true });

		const onVisibilityChange = () => {
			if (document.hidden) {
				cancelAnimationFrame(rafRef.current);
			} else {
				rafRef.current = requestAnimationFrame(stepFrame);
			}
		};
		document.addEventListener("visibilitychange", onVisibilityChange);
		rafRef.current = requestAnimationFrame(stepFrame);

		return () => {
			cancelAnimationFrame(rafRef.current);
			window.removeEventListener("resize", resize);
			window.removeEventListener("pointermove", onMove);
			window.removeEventListener("pointerdown", onDown);
			window.removeEventListener("pointerup", onUp);
			document.removeEventListener("visibilitychange", onVisibilityChange);
		};
	}, [densityScale, showLogo]);

	return <canvas ref={canvasRef} className="fixed inset-0 -z-10" aria-hidden="true" />;
}

export default function App() {
	const [showIndicator, setShowIndicator] = useState(true);
	const [selectedProject, setSelectedProject] = useState(null);
	const [selectedService, setSelectedService] = useState(null);
	const [serviceAnchor, setServiceAnchor] = useState(null);
	const [activeSection, setActiveSection] = useState('home');
	const scrollRef = useRef(null);

	// Controls for floating services model
	const [modelFit, setModelFit] = useState(0.53);
	const [modelTiltX, setModelTiltX] = useState(0.08);
	const [modelTiltY, setModelTiltY] = useState(-3.14);
	const [modelTiltZ, setModelTiltZ] = useState(-0.34);
	const [modelSpeed, setModelSpeed] = useState(0.26);
	const [modelAngle, setModelAngle] = useState(-0.7);
	const [modelOffsetX, setModelOffsetX] = useState(-0.065);
	const [modelOffsetY, setModelOffsetY] = useState(0);
	const [modelOffsetZ, setModelOffsetZ] = useState(0);
	const [showModelControls, setShowModelControls] = useState(false);

	// Convention: newest projects go FIRST (index 0). Older work shifts down.
	const projects = [
		{ id: 5, title: "QuickSpool", description: "Heavy-duty string spool for concrete crews" },
		{ id: 4, title: "Hall", description: "Rifle stock scanning & CAD recreation" },
		{ id: 1, title: "Merigold", description: "Restoration of a 100+ year old church" },
		{ id: 2, title: "UMMC", description: "Custom surgical camera mounting system" },
		{ id: 3, title: "Turcotte", description: "Reverse engineering of prosthetic cover" }
	];

	// BEYOND section projects (personal / R&D work). Kept separate from `projects`
	// so the Works grid's positional indexing stays untouched.
	const beyondProjects = [
		{ id: 6, title: "PS-250", description: "A low-cost BC-250 board reborn as a couch-ready console" },
		{ id: 7, title: "idea-gen", description: "A handheld device for creativity by word combination" }
	];

	// Color schemes for each section (warm to cool)
	const sectionColors = {
		home: {
			palette: ["#ffb347", "#ff7f50", "#ff6347", "#ff4500", "#ffa500"],
			gradient: { start: { h: 15, s: 90, l: 50 }, end: { h: 30, s: 90, l: 60 } }
		},
		// Services adopts previous Works gradient
		services: {
			palette: ["#fde047", "#fbbf24", "#7fffd4", "#5eead4", "#22d3ee"],
			gradient: { start: { h: 150, s: 70, l: 45 }, end: { h: 210, s: 80, l: 40 } }
		},
		// Works adopts previous Contact gradient
		works: {
			palette: ["#22c55e", "#10b981", "#0ea5e9", "#2563eb", "#1d4ed8"],
			gradient: { start: { h: 210, s: 75, l: 35 }, end: { h: 270, s: 80, l: 30 } }
		},
		// New deeper, darker Contact gradient
		contact: {
			palette: ["#0ea5e9", "#2563eb", "#1d4ed8", "#4338ca", "#6d28d9"],
			gradient: { start: { h: 230, s: 85, l: 22 }, end: { h: 265, s: 90, l: 16 } }
		},
		// BEYOND: bright white showcase — blue/black dots, grey topo lines (see lightMode)
		beyond: {
			palette: ["#0051ff", "#0a0a0a", "#1d4ed8", "#111827", "#2563eb"],
			gradient: { start: { h: 210, s: 20, l: 100 }, end: { h: 210, s: 12, l: 96 } }
		}
	};

	useEffect(() => {
		const container = scrollRef.current;
		if (!container) return;

		const handleScroll = () => {
			const sections = ['home', 'services', 'works', 'contact', 'beyond'];
			const mid = container.scrollTop + container.clientHeight / 2;
			sections.forEach((section) => {
				const el = document.getElementById(section);
				if (!el) return;
				const top = el.offsetTop;
				const height = el.offsetHeight;
				if (mid >= top && mid < top + height) {
					setActiveSection(section);
				}
			});
		};

		container.addEventListener('scroll', handleScroll, { passive: true });
		handleScroll();
		return () => {
			container.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const scrollToSection = (sectionId) => {
		const element = document.getElementById(sectionId);
		if (element) {
			element.scrollIntoView({ behavior: 'smooth' });
		}
	};

	const indicatorTextMap = { home: 'Take a dip', services: 'Hold Your Breath', works: 'Keep Swimming' };

	return (
		<div className="relative text-white">
			<PointsUniverse 
				theme="fever" 
				densityScale={2.8} 
				palette={sectionColors[activeSection].palette}
				gradientColors={sectionColors[activeSection].gradient}
				hueDrift={activeSection === 'home' ? 0 : 10}
				lightMode={activeSection === 'beyond'}
			/>
			
			{/* Navigation */}
			<nav className="fixed top-4 right-4 md:top-8 md:right-8 z-40 flex gap-3 md:gap-6" aria-label="Main navigation">
				{['home', 'services', 'works', 'contact', 'beyond'].map((section) => (
					<button
						key={section}
						onClick={() => scrollToSection(section)}
						className={`text-xs md:text-sm uppercase tracking-wider font-medium transition-all ${activeSection === 'beyond' ? 'text-black/70 hover:text-black' : 'text-white/80 hover:text-white'}`}
						aria-label={`Navigate to ${section}`}
					>
						{section}
					</button>
				))}
			</nav>

			{/* Global Scroll Indicator */}
			{indicatorTextMap[activeSection] && (
				<div className="fixed left-1/2 -translate-x-1/2 bottom-20 text-white/90 select-none pointer-events-none z-10">
					<div className="flex flex-col items-center gap-2">
						<svg
							className="w-6 h-6 opacity-90 motion-safe:animate-bounce"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							aria-hidden="true"
						>
							<path d="M12 5v14m0 0l-7-7m7 7l7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
						</svg>
						<span className="text-sm tracking-widest uppercase font-medium opacity-80">{indicatorTextMap[activeSection]}</span>
					</div>
				</div>
			)}

			{/* Main Content Container with Scroll Snap */}
			<div ref={scrollRef} className="h-screen overflow-y-auto overflow-x-hidden scroll-smooth" style={{ scrollSnapType: 'y mandatory', WebkitOverflowScrolling: 'touch' }}>
				
				{/* Home Section */}
				<section id="home" className="relative min-h-screen flex items-center justify-center" style={{ scrollSnapAlign: 'start' }}>
					<h1 className="sr-only">POINTS Engineering — 3D Scanning, CAD Modeling, Reverse Engineering &amp; Prototyping in Madison, MS</h1>
					<div className="w-full max-w-6xl h-64">
						<PointsLogo />
					</div>
				</section>

				{/* Floating model controls toggle and overlay (Services only) */}
				{false && activeSection === 'services' && (
					<>
						<button
							onClick={() => setShowModelControls((v) => !v)}
							className="fixed z-40 bottom-6 right-6 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-4 py-2 backdrop-blur-md"
						>
							Model Controls
						</button>
						{showModelControls && (
							<div className="fixed inset-0 z-50" onClick={() => setShowModelControls(false)}>
								<div
									onClick={(e) => e.stopPropagation()}
									className="fixed bottom-20 right-6 w-[92vw] max-w-3xl bg-white/10 border border-white/20 rounded-2xl p-4 md:p-6 backdrop-blur-md text-white shadow-xl"
								>
									<h3 className="text-lg font-bold mb-4 uppercase tracking-wide">Model Controls</h3>
									<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
										<label className="block">
											<span className="text-sm text-white/70">Fit</span>
											<span className="text-xs text-white/60 ml-2">{modelFit.toFixed(2)}</span>
											<input type="range" min="0.3" max="1.0" step="0.01" value={modelFit} onChange={(e) => setModelFit(parseFloat(e.target.value))} className="w-full" />
										</label>
										<label className="block">
											<span className="text-sm text-white/70">Rotate Speed</span>
											<span className="text-xs text-white/60 ml-2">{modelSpeed.toFixed(2)}</span>
											<input type="range" min="0" max="1" step="0.01" value={modelSpeed} onChange={(e) => setModelSpeed(parseFloat(e.target.value))} className="w-full" />
										</label>
										<label className="block">
											<span className="text-sm text-white/70">Initial Angle</span>
											<span className="text-xs text-white/60 ml-2">{modelAngle.toFixed(2)}</span>
											<input type="range" min="-3.14" max="3.14" step="0.01" value={modelAngle} onChange={(e) => setModelAngle(parseFloat(e.target.value))} className="w-full" />
										</label>
									</div>
									<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
										<label className="block">
											<span className="text-sm text-white/70">Tilt X</span>
											<span className="text-xs text-white/60 ml-2">{modelTiltX.toFixed(2)}</span>
											<input type="range" min="-1.57" max="1.57" step="0.01" value={modelTiltX} onChange={(e) => setModelTiltX(parseFloat(e.target.value))} className="w-full" />
										</label>
										<label className="block">
											<span className="text-sm text-white/70">Tilt Y</span>
											<span className="text-xs text-white/60 ml-2">{modelTiltY.toFixed(2)}</span>
											<input type="range" min="-3.14" max="3.14" step="0.01" value={modelTiltY} onChange={(e) => setModelTiltY(parseFloat(e.target.value))} className="w-full" />
										</label>
										<label className="block">
											<span className="text-sm text-white/70">Tilt Z</span>
											<span className="text-xs text-white/60 ml-2">{modelTiltZ.toFixed(2)}</span>
											<input type="range" min="-3.14" max="3.14" step="0.01" value={modelTiltZ} onChange={(e) => setModelTiltZ(parseFloat(e.target.value))} className="w-full" />
										</label>
									</div>
									<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
										<label className="block">
											<span className="text-sm text-white/70">Offset X</span>
											<span className="text-xs text-white/60 ml-2">{modelOffsetX.toFixed(3)}</span>
											<input type="range" min="-1" max="1" step="0.01" value={modelOffsetX} onChange={(e) => setModelOffsetX(parseFloat(e.target.value))} className="w-full" />
										</label>
										<label className="block">
											<span className="text-sm text-white/70">Offset Y</span>
											<span className="text-xs text-white/60 ml-2">{modelOffsetY.toFixed(3)}</span>
											<input type="range" min="-1" max="1" step="0.01" value={modelOffsetY} onChange={(e) => setModelOffsetY(parseFloat(e.target.value))} className="w-full" />
										</label>
										<label className="block">
											<span className="text-sm text-white/70">Offset Z</span>
											<span className="text-xs text-white/60 ml-2">{modelOffsetZ.toFixed(3)}</span>
											<input type="range" min="-1" max="1" step="0.01" value={modelOffsetZ} onChange={(e) => setModelOffsetZ(parseFloat(e.target.value))} className="w-full" />
										</label>
									</div>
								</div>
							</div>
						)}
					</>
				)}

				{/* Services Section */}
				<section id="services" className="relative min-h-screen flex items-center justify-center px-4 md:px-8 py-10" style={{ scrollSnapAlign: 'start' }}>
					<div className="max-w-7xl mx-auto w-full">
						<h2 className="text-5xl md:text-7xl font-black mb-2 text-left uppercase tracking-tight text-white">
							Services
						</h2>
						<p className="text-sm text-white/50 mb-6 tracking-wide">Click a service to learn more.</p>

						{(() => {
							const services = [
								{ id: 1, title: '3D Scanning', subtitle: 'High-fidelity capture of real-world geometry', details: [
									'We capture accurate geometry and textures to create digital twins ready for engineering, analysis, and visualization.',
									'Our scanning workflow supports everything from small parts to architectural spaces.'
								]},
								{ id: 2, title: '3D Modeling', subtitle: 'Design and surfacing for production and visualization', details: [
									'We build precise CAD models in SolidWorks and create expressive visuals in Blender depending on the need.',
									'Expect clean topology, watertight geometry, and deliverables tailored to manufacturing or rendering.'
								]},
								{ id: 3, title: 'Reverse Engineering', subtitle: 'From physical to digital', details: [
									'We reconstruct parts using 3D scanning, silicone molding, and advanced surfacing techniques.',
									'Clients receive fully parametric CAD or high-quality meshes ready for downstream work.'
								]},
								{ id: 4, title: 'Prototyping', subtitle: 'Fast iteration to real parts', details: [
									'We prototype using our Bambu X1C printer and collaborate with local metal fabricators for robust components.',
									'Use prototypes to validate fit, ergonomics, mechanics, and overall product feel.'
								]},
								{ id: 5, title: 'Simulation', subtitle: 'See it move, stress, and perform', details: [
									'We provide visual animation and mechanical simulations with options for structural analysis (FEA).',
									'Output formats include videos, interactive viewers, and engineering-ready documentation.'
								]},
							];

							const onServiceClick = (service, e) => {
								const rect = e.currentTarget.getBoundingClientRect();
								setSelectedService(service);
								setServiceAnchor({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
							};

							return (
								<div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
									{services.filter(svc => svc.id !== 5).map((svc, idx) => (
										<button
											key={svc.id}
											onClick={(e) => onServiceClick(svc, e)}
											className="group relative h-[35vh] sm:h-[38vh] lg:h-[55vh] bg-white/5 backdrop-blur-sm border border-white/20 rounded-2xl overflow-hidden hover:bg-white/10 hover:scale-[1.02] transition-all duration-300 text-left"
										>
											{/* Tile background */}
											<div className="absolute inset-0">
												<div
													className="absolute inset-0 bg-center bg-cover"
													style={{
														backgroundImage: `url('${svc.id === 1 ? "/3dscanning.png" : svc.id === 2 ? "/cad.png" : svc.id === 3 ? "/reverse.png" : "/proto.png"}')`,
														backgroundPosition: '50% 50%'
													}}
												/>
											</div>
											{/* Dark gradient for legibility */}
											<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

											{/* Text */}
											<div className="absolute bottom-3 left-3 md:bottom-6 md:left-6 max-w-[90%]">
												<h3 className="text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tight text-white mb-1">{svc.title}</h3>
												<p className="text-white/80 text-xs sm:text-sm lg:text-base hidden sm:block">{svc.subtitle}</p>
											</div>
											<div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
												<svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
													<path d="M7 17L17 7M17 7H7M17 7V17" />
												</svg>
											</div>
										</button>
									))}
								</div>
							);
						})()}

					</div>
				</section>

				{/* Works Section: full-bleed floating models with text overlays */}
				<section id="works" className="relative min-h-screen flex items-start md:items-center justify-center px-4 md:px-8 py-8 md:py-0" style={{ scrollSnapAlign: 'start' }}>
					<div className="max-w-7xl mx-auto w-full">
						<h2 className="text-5xl md:text-7xl font-black mb-2 text-left uppercase tracking-tight text-white">Selected Works</h2>
						<p className="hidden md:block text-sm text-white/50 mb-8 tracking-wide">Drag to rotate. Scroll to zoom. Click for details.</p>
						<p className="block md:hidden text-sm text-white/50 mb-8 tracking-wide">Tap for details.</p>
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-8">
							{/* QuickSpool slot */}
							<div className="relative h-[45vh] sm:h-[50vh] lg:h-[60vh]">
								<MarigoldViewer onClick={() => setSelectedProject(projects[0])} className="absolute inset-0 cursor-pointer" fitMultiplier={0.8} modelUrl="/quickspool.glb" />
								<div className="absolute bottom-4 left-4 md:bottom-6 md:left-6 pointer-events-none">
									<h3 className="text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-wide">{projects[0].title}</h3>
									<p className="text-white/85 text-sm md:text-base">{projects[0].description}</p>
								</div>
							</div>

							{/* Hall rifle stock slot */}
							<div className="relative h-[45vh] sm:h-[50vh] lg:h-[60vh]">
								<MarigoldViewer onClick={() => setSelectedProject(projects[1])} className="absolute inset-0 cursor-pointer" fitMultiplier={0.8} modelUrl="/gun.glb" />
								<div className="absolute bottom-4 left-4 md:bottom-6 md:left-6 pointer-events-none">
									<h3 className="text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-wide">{projects[1].title}</h3>
									<p className="text-white/85 text-sm md:text-base">{projects[1].description}</p>
								</div>
							</div>

							{/* Marigold model slot */}
							<div className="relative h-[45vh] sm:h-[50vh] lg:h-[60vh]">
								<MarigoldViewer onClick={() => setSelectedProject(projects[2])} className="absolute inset-0 cursor-pointer" fitMultiplier={1.4} />
								<div className="absolute bottom-4 left-4 md:bottom-6 md:left-6">
									<h3 className="text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-wide">{projects[2].title}</h3>
									<p className="text-white/85 text-sm md:text-base">{projects[2].description}</p>
								</div>
							</div>

							{/* UMMC Surgitel model slot */}
							<div className="relative h-[45vh] sm:h-[50vh] lg:h-[60vh]">
								<MarigoldViewer onClick={() => setSelectedProject(projects[3])} className="absolute inset-0 cursor-pointer" fitMultiplier={1.1} modelUrl="/surgitelpreview.glb" />
								<div className="absolute bottom-4 left-4 md:bottom-6 md:left-6 pointer-events-none">
									<h3 className="text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-wide">{projects[3].title}</h3>
									<p className="text-white/85 text-sm md:text-base">{projects[3].description}</p>
								</div>
							</div>

							{/* Prosthetics slot */}
							<div className="relative h-[45vh] sm:h-[50vh] lg:h-[60vh]">
								<MarigoldViewer onClick={() => setSelectedProject(projects[4])} className="absolute inset-0 cursor-pointer" fitMultiplier={0.8} modelUrl="/prosthetic.glb" />
								<div className="absolute bottom-4 left-4 md:bottom-6 md:left-6 pointer-events-none">
									<h3 className="text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-wide">{projects[4].title}</h3>
									<p className="text-white/85 text-sm md:text-base">{projects[4].description}</p>
								</div>
							</div>
						</div>
					</div>
				</section>

				{/* Contact Section */}
				<section id="contact" className="relative min-h-screen flex items-center justify-center px-4 md:px-8" style={{ scrollSnapAlign: 'start' }}>
					<div className="text-center w-full max-w-2xl mx-auto">
						<h2 className="text-3xl md:text-6xl font-black mb-8 uppercase tracking-tight text-white">
							We are points,<br/>
							let us make yours.
						</h2>
						<a href="mailto:david@points.engineering" className="block text-lg sm:text-2xl md:text-4xl font-black mb-4 text-white hover:text-white/80 transition-colors break-all md:break-normal">DAVID@POINTS.ENGINEERING</a>
						<a href="tel:+13109759300" className="block text-xl md:text-3xl font-bold mb-2 text-white hover:text-white/80 transition-colors">310-975-9300</a>
						<address className="text-lg md:text-xl font-semibold mb-12 text-white/90 tracking-wide not-italic">MADISON, MS</address>
						<div className="inline-block bg-white/5 backdrop-blur-sm border border-white/20 rounded-2xl px-8 py-4 hover:bg-white/10 transition-all">
							<p className="font-medium uppercase tracking-wide text-sm text-white/80">Email integration coming soon</p>
						</div>
						<div className="mt-12 text-center text-white/70 text-sm">
							<p>© 2025 Poisson LLC d/b/a POINTS. All rights reserved.</p>
						</div>
					</div>
				</section>

				{/* Beyond Section: light-themed showcase of recent personal / R&D projects */}
				<section id="beyond" className="relative min-h-screen flex items-center justify-center px-4 md:px-8 py-10" style={{ scrollSnapAlign: 'start' }}>
					<div className="max-w-7xl mx-auto w-full">
						<h2 className="text-5xl md:text-7xl font-black mb-2 text-left uppercase tracking-tight text-black">Beyond</h2>
						<p className="text-sm md:text-base text-black/60 mb-8 tracking-wide">Here are some selected recent projects.</p>

						<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10">
							{beyondProjects.map((proj) => (
								<div key={proj.id} className="bg-black/[0.04] border border-black/10 rounded-2xl overflow-hidden">
									<div className="relative h-[42vh] sm:h-[48vh]">
										<PlainModelViewer
											onClick={() => setSelectedProject(proj)}
											className="absolute inset-0 cursor-pointer"
											fitMultiplier={0.8}
											modelUrl={proj.id === 6 ? "/ps250.glb" : "/tcomb.glb"}
											transparentMaterials={proj.id === 7 ? ['Material.001'] : []}
										/>
									</div>
									<button
										onClick={() => setSelectedProject(proj)}
										className="block w-full text-left p-5 md:p-6 hover:bg-black/[0.03] transition-colors"
									>
										<h3 className="text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-wide text-black">{proj.title}</h3>
										<p className="text-black/70 text-sm md:text-base mt-1">{proj.description}</p>
									</button>
								</div>
							))}
						</div>
					</div>
				</section>
			</div>

			{/* Project Modal */}
			<ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
			<ServiceModal service={selectedService} anchor={serviceAnchor} onClose={() => { setSelectedService(null); setServiceAnchor(null); }} />
		</div>
	);
}


